import { html, insertHtml, joinHtml, setHtml } from "./html.js";
import depthLevelsFeet from "../../greatlakes/depths.json" with { type: "json" };
import forecastOffsetHours from "../../greatlakes/forecast_offsets.json" with { type: "json" };
import { L } from "./vendor.js";
import { setupWind, refreshWind, showWindPayload, windEnabled, windReading, windSpeedLabel } from "./wind.js";
import { ui } from "./state.js";
import { convertUnitValue, currentChopRanges, formatUnitValue, unitPreference } from "./units.js";
import { depthRangeLabel, showWaterColumnDialog, showWaterColumnUnavailableDialog, thermoclineBand, thicknessLabel } from "./water-column.js";
import { currentPixelsPerFrame, dramaticParticleSpeed } from "./particle-speed.js";
import { TEMPERATURE_COLOR_RANGE_C, WAVE_DEFAULT_MAX_METERS, WIND_DEFAULT_MAX_METERS_PER_SECOND, createPaletteFilter, fitPaletteToView, paletteGradient, paletteOverlays, savedTemperatureColorMode, waveStops, windStops } from "./great-lakes-palette.js";
import { currentProfileActionHtml, directionIconHtml, pointForecastActionHtml, profileActionHtml, readingHtml } from "./cards.js";
import { appDateParts, appDayKey, formatAppDate, formatAppDateTime, formatAppTime } from "./app-time.js";
import {
  currentDirectionLabel, currentProfileDepthLabel, currentSpeedLabel,
  currentProfileHtml as sharedCurrentProfileHtml
} from "./current-profile.js";
export { currentColor, currentDirectionLabel, currentProfileDepthLabel, currentSpeedLabel } from "./current-profile.js";
import {
  clearGreatLakesAnimationVisuals,
  greatLakesAnimationActive,
  greatLakesAnimationForecastHour,
  greatLakesAnimationPointParams,
  greatLakesTimelineHtml,
  loadGreatLakesAnimation,
  setupGreatLakesTimeline,
  stopGreatLakesAnimation,
  syncTimelineControls
} from "./great-lakes-animation.js";

// NOAA Great Lakes OFS map layers. Temperature, thermocline depth, current
// speed, and wave height are server-rendered rasters clipped to NOAA's water
// mask; currents add an animated particle flow (or static arrows) on top of
// the speed shading, and waves show animated travel or static direction arrows.
export let greatLakesConditionsControl = null;
export let greatLakesConditionsLayer = null;
export let greatLakesConditionsRequest = null;
export let greatLakesActiveLayer = "";
export let greatLakesRasterReloadTimer = null;
export let greatLakesParticleLayer = null;
export let greatLakesLoadedModelsKey = "";
export let greatLakesLoadRevision = 0;
export const GREAT_LAKES_CLIENT_CACHE_MS = 10 * 60 * 1000;
export const GREAT_LAKES_CLIENT_CACHE_LIMIT = 12;
// NOAA's distinct native depth levels, converted to feet and rounded for the
// slider. The same list drives background preparation and cached map views.
export const GREAT_LAKES_DEPTH_LEVELS_FEET = Object.freeze(depthLevelsFeet);
export const GREAT_LAKES_MAX_DEPTH_METERS = GREAT_LAKES_DEPTH_LEVELS_FEET.at(-1) * 0.3048;
export const GREAT_LAKES_DEPTH_SLIDER_MAX = GREAT_LAKES_DEPTH_LEVELS_FEET.length - 1;
// Forecast presets, shared with the server so these one-click times are
// already downloaded and drawn before the first click. Every other hour up to
// GREAT_LAKES_MAX_FORECAST_HOUR is still selectable on the slider and is built
// on demand the first time it is asked for.
export const GREAT_LAKES_FORECAST_OFFSETS = Object.freeze([...forecastOffsetHours]);
// NOAA's lake models forecast 120 hours ahead; the wave model only 48.
export const GREAT_LAKES_MAX_FORECAST_HOUR = 120;
export const GREAT_LAKES_LAYER_MAX_FORECAST_HOURS = Object.freeze({ waves: 48, "air-temperature": 48 });
let airTemperatureMaxForecastHour = 48;

export function greatLakesMaxForecastHour(layer) {
  if (layer === "air-temperature") return airTemperatureMaxForecastHour;
  return GREAT_LAKES_LAYER_MAX_FORECAST_HOURS[layer] ?? GREAT_LAKES_MAX_FORECAST_HOUR;
}

export function setAirTemperatureForecastHorizon(metadata = {}) {
  const maximum = Number(metadata.maxForecastHour);
  if (metadata.maxForecastHour == null || !Number.isFinite(maximum)) return;
  airTemperatureMaxForecastHour = Math.max(0, Math.min(48, Math.floor(maximum)));
  syncGreatLakesLayerControls();
  syncTimelineControls();
}

export function greatLakesAirTemperatureRasters(payload) {
  if (greatLakesControlValue("air-coverage") === "lakes" && payload?.metadata?.lakeCoverageAvailable === false) {
    throw new Error("Lakes-only air temperature is unavailable. Choose regional coverage or try again later.");
  }
  return (greatLakesControlValue("air-coverage") === "lakes" ? payload?.lakeRasters : payload?.rasters) || [];
}
export const greatLakesPayloadCache = new Map();

export function cachedGreatLakesPayload(key) {
  const now = Date.now();
  for (const [storedKey, entry] of greatLakesPayloadCache) {
    if (now - entry.createdAt >= GREAT_LAKES_CLIENT_CACHE_MS) greatLakesPayloadCache.delete(storedKey);
  }
  const entry = greatLakesPayloadCache.get(key);
  if (!entry) return null;
  greatLakesPayloadCache.delete(key);
  greatLakesPayloadCache.set(key, entry);
  return entry.payload;
}

export function cacheGreatLakesPayload(key, payload) {
  cachedGreatLakesPayload(key);
  greatLakesPayloadCache.set(key, { payload, createdAt: Date.now() });
  while (greatLakesPayloadCache.size > GREAT_LAKES_CLIENT_CACHE_LIMIT) {
    greatLakesPayloadCache.delete(greatLakesPayloadCache.keys().next().value);
  }
}
export const CURRENT_BACKGROUND_STORAGE_KEY = "glc.CurrentBackground";
export const FLOW_COLOR_STORAGE_KEY = "glc.FlowColor";
export const WIND_PARTICLE_COLOR_STORAGE_KEY = "glc.WindParticleColor";
export const WIND_DISPLAY_STORAGE_KEY = "glc.WindDisplay";
export const WAVE_PARTICLE_COLOR_STORAGE_KEY = "glc.WaveParticleColor";
// "speed" colours each flow line by its current speed (the speed legend's palette).
export const FLOW_COLOR_BY_SPEED = "speed";
export const FLOW_COLOR_OPTIONS = Object.freeze([
  [FLOW_COLOR_BY_SPEED, "By current speed"], ["#ffffff", "White"], ["#111827", "Black"], ["#9ca3af", "Grey"],
  ["#ef4444", "Red"], ["#2563eb", "Blue"], ["#facc15", "Yellow"], ["#22c55e", "Green"]
]);
export const DEFAULT_WIND_PARTICLE_COLOR = "#67e8f9";
export const WIND_PARTICLE_COLOR_OPTIONS = Object.freeze([
  [DEFAULT_WIND_PARTICLE_COLOR, "Cyan"], ...FLOW_COLOR_OPTIONS.filter(([value]) => value !== FLOW_COLOR_BY_SPEED),
  ["#ffd700", "Gold"], ["#a855f7", "Purple"], ["#ec4899", "Pink"]
]);
export const DEFAULT_WAVE_PARTICLE_COLOR = "#ffffff";
export const WAVE_PARTICLE_COLOR_OPTIONS = WIND_PARTICLE_COLOR_OPTIONS;
export const CURRENT_BACKGROUND_OPTIONS = Object.freeze([["speed", "Current speed"], ["temperature", "Water temperature"], ["none", "None (map only)"]]);
export const DEFAULT_FLOW_COLOR = "#ffffff";
export let greatLakesFlowColor = DEFAULT_FLOW_COLOR;
export const WAVE_DISPLAY_STORAGE_KEY = "glc.WaveDisplay";
export let greatLakesWaveArrowLayer = null;
// Currents use one fixed raster resolution, so a temperature background
// matches it and does not need reloading (and restarting the flow) on zoom.
export const CURRENT_BACKGROUND_RESOLUTION = 512;
// One drawing per layer, forecast time, and depth: every lake at full detail.
// The server draws exactly this ahead of time, and the map never has to ask
// again when the view changes.
export const GREAT_LAKES_ALL_MODELS = Object.freeze(["LSOFS", "LMHOFS", "LEOFS", "LOOFS"]);
export const GREAT_LAKES_LAYER_RESOLUTION = 512;

export const GREAT_LAKES_MODEL_BOUNDS = {
  LSOFS: [[46.0, -93.5], [49.4, -83.5]],
  LMHOFS: [[41.2, -88.5], [46.8, -80.0]],
  LEOFS: [[41.0, -84.8], [43.5, -78.3]],
  LOOFS: [[42.4, -80.4], [44.7, -75.3]]
};

export const LAYER_STORAGE_KEY = "glc.Layer";
// The site opens on surface temperature; the choice is remembered.
export const LAYER_OPTIONS = Object.freeze([
  ["temperature", "Water temperature"], ["bottom-temperature", "Bottom temperatures"], ["air-temperature", "Air temperature"], ["target-temperature", "Target-temperature depth"], ["thermocline", "Thermocline depth"], ["currents", "Currents"], ["waves", "Waves"], ["wind", "Wind"], ["upwelling", "Upwelling & downwelling"], ["", "None"]
]);
export const DEFAULT_LAYER = "temperature";
export let targetTemperatureC = 80 / 9;
let targetDepthBounds = null;
let targetSurfaceTemperatureBounds = null;
let targetSurfaceTemperatureRangeState = "loading";

export function setTargetTemperature(value) {
  if (Number.isFinite(value) && value >= 0 && value <= 30) targetTemperatureC = value;
  syncTargetTemperatureUnits();
}

export function syncTargetTemperatureUnits() {
  const input = document.querySelector("[data-gl-target-temperature]");
  if (!input) return;
  const fahrenheit = unitPreference("waterTemperature") === "F";
  input.min = fahrenheit ? "32" : "0";
  input.max = fahrenheit ? "86" : "30";
  input.step = "any";
  input.value = String(Number((fahrenheit ? targetTemperatureC * 1.8 + 32 : targetTemperatureC).toFixed(2)));
  document.querySelector("[data-gl-target-unit]").textContent = fahrenheit ? "°F" : "°C";
  setTargetTemperatureLegendRange(targetDepthBounds);
  renderTargetTemperatureSurfaceRange();
}

export function savedLayer() {
  try {
    const saved = localStorage.getItem(LAYER_STORAGE_KEY);
    return LAYER_OPTIONS.some(([value]) => value === saved) ? saved : DEFAULT_LAYER;
  } catch { return DEFAULT_LAYER; }
}

export function layerOptionsHtml(selected) {
  return joinHtml(LAYER_OPTIONS.map(([value, label]) => html`<option value="${value}"${value === selected ? " selected" : ""}>${label}</option>`), "");
}

// "Past 90 days": the server keeps four samples a day of "Now" (never
// forecasts). The map shows the saved surface maps for the chosen time, and
// point readings and stations come from that sample too.
export const HISTORY_FORECAST_VALUE = "history";
const HOUR_MS = 3600 * 1000;
export let greatLakesHistoryIndex = null;
export let greatLakesHistoryMs = null;
let greatLakesHistoryIndexAt = 0;
// Four samples are saved per day; refresh this list on entering history.
const HISTORY_INDEX_MAX_AGE_MS = 2 * 60 * 1000;

const CALENDAR_ICON = html`<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15" rx="2.5"/><path d="M3.5 10h17M8 3v4m8-4v4"/></svg>`;

export function greatLakesHistoryHtml() {
  const step = (hours, label, symbol) => html`<button type="button" data-gl-history-step="${hours}" aria-label="${label}" title="${label}">${symbol}</button>`;
  return html`<div class="great-lakes-history" data-gl-history-controls>
    <div class="great-lakes-history-picker">
      ${step(-24, "One day earlier", "«")}${step(-1, "Previous saved time", "‹")}
      <button type="button" class="great-lakes-history-when" data-gl-history-open aria-expanded="false" aria-controls="greatLakesHistoryCalendar" aria-label="Choose a saved date and time"><span data-gl-history-label>Choose a time</span>${CALENDAR_ICON}</button>
      ${step(1, "Next saved time", "›")}${step(24, "One day later", "»")}
    </div>
    <div class="great-lakes-history-calendar" id="greatLakesHistoryCalendar" data-gl-history-calendar hidden></div>
  </div>`;
}

export function greatLakesHistoryMode() {
  return greatLakesControlValue("forecast") === HISTORY_FORECAST_VALUE;
}

export function historyIso(milliseconds) {
  return new Date(milliseconds).toISOString().replace(/\.\d{3}Z$/, "Z");
}

// The selected saved sample on the map (ISO 8601), or null when showing "Now" or a forecast.
export function greatLakesHistoryTime() {
  return greatLakesHistoryMode() && Number.isFinite(greatLakesHistoryMs) ? historyIso(greatLakesHistoryMs) : null;
}

// Point lookups follow a saved history sample or the animation's exact inputs.
export function greatLakesTimeParams() {
  const time = greatLakesHistoryTime();
  return time ? { time } : greatLakesAnimationPointParams();
}

// Saved times (milliseconds, oldest first) that have a map of `layer`.
export function historyHours(index, layer = "") {
  return (index?.hours || [])
    .filter((hour) => !layer || (hour.layers || []).includes(layer))
    .map((hour) => Date.parse(hour.time))
    .filter(Number.isFinite)
    .sort((first, second) => first - second);
}

export function nearestHistoryHour(hours, target) {
  if (!hours.length) return null;
  return hours.reduce((best, hour) => Math.abs(hour - target) < Math.abs(best - target) ? hour : best, hours[0]);
}

// Move by `stepHours`, landing on the saved hour nearest that time (always moving, if there is anywhere to go).
export function stepHistoryHour(hours, current, stepHours) {
  const ahead = hours.filter((hour) => (stepHours > 0 ? hour > current : hour < current));
  if (!ahead.length) return current;
  return nearestHistoryHour(ahead, current + stepHours * HOUR_MS);
}

const pad = (value) => String(value).padStart(2, "0");
const calendarDayKey = (year, month, day) => `${year}-${pad(month)}-${pad(day)}`;

// A Toronto calendar day, "2026-10-05".
export function localDayKey(milliseconds) {
  return appDayKey(milliseconds);
}

// Saved hours grouped by local day: Map of day key to that day's hours, oldest first.
export function historyDays(hours) {
  const days = new Map();
  hours.forEach((hour) => {
    const key = localDayKey(hour);
    if (!days.has(key)) days.set(key, []);
    days.get(key).push(hour);
  });
  return days;
}

const monthStart = (milliseconds) => {
  const parts = appDateParts(milliseconds);
  return parts ? Date.UTC(parts.year, parts.month - 1, 1, 12) : Number.NaN;
};
const shiftMonth = (start, months) => {
  const date = new Date(start);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1, 12);
};

// The calendar of saved days: only days with a saved sample can be picked; the chosen day lists its times.
export function historyCalendarHtml(hours, { month, day, selected } = {}) {
  if (!hours.length) return html`<p class="gl-cal-empty">No saved samples yet.</p>`;
  const days = historyDays(hours);
  const shownMonth = Number.isFinite(month) ? month : monthStart(selected ?? hours[hours.length - 1]);
  const firstMonth = monthStart(hours[0]), lastMonth = monthStart(hours[hours.length - 1]);
  const first = new Date(shownMonth);
  const year = first.getUTCFullYear(), monthNumber = first.getUTCMonth() + 1;
  const blanks = first.getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, monthNumber, 0, 12)).getUTCDate();
  const selectedDay = Number.isFinite(selected) ? localDayKey(selected) : "";
  const today = localDayKey(Date.now());
  const weekdays = joinHtml(["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((name) => html`<span class="gl-cal-dow" aria-hidden="true">${name}</span>`), "");
  const cells = [];
  for (let blank = 0; blank < blanks; blank += 1) cells.push(html`<span aria-hidden="true"></span>`);
  for (let date = 1; date <= daysInMonth; date += 1) {
    const time = Date.UTC(year, monthNumber - 1, date, 12);
    const key = calendarDayKey(year, monthNumber, date);
    const saved = days.get(key);
    const classes = ["gl-cal-day", saved ? "has-data" : "", key === day ? "is-open" : "", key === selectedDay ? "is-selected" : "", key === today ? "is-today" : ""].filter(Boolean).join(" ");
    const label = formatAppDate(time, { weekday: "long" });
    cells.push(saved
      ? html`<button type="button" class="${classes}" data-gl-cal-day="${key}" aria-label="${label}, ${saved.length} saved ${saved.length === 1 ? "hour" : "hours"}"${key === day ? html` aria-pressed="true"` : ""}>${date}</button>`
      : html`<button type="button" class="${classes}" disabled aria-label="${label}, nothing saved">${date}</button>`);
  }
  const monthLabel = formatAppDate(Date.UTC(year, monthNumber - 1, 1, 12), { day: undefined, year: "numeric" });
  const dayHours = day ? days.get(day) || [] : [];
  const hourList = dayHours.length ? html`<div class="gl-cal-hours">
      <span class="gl-cal-hours-title">${formatAppDate(dayHours[0], { weekday: "long" })}</span>
      <div class="gl-cal-hour-grid">${joinHtml(dayHours.map((hour) => html`<button type="button" class="gl-cal-hour${hour === selected ? " is-selected" : ""}" data-gl-cal-hour="${hour}"${hour === selected ? html` aria-pressed="true"` : ""}>${formatAppTime(hour, { minute: undefined })}</button>`), "")}</div>
    </div>` : html`<p class="gl-cal-hint">Pick a highlighted day.</p>`;
  return html`<div class="gl-cal-head">
      <button type="button" data-gl-cal-month="-1" aria-label="Previous month"${shownMonth <= firstMonth ? " disabled" : ""}>‹</button>
      <strong aria-live="polite">${monthLabel}</strong>
      <button type="button" data-gl-cal-month="1" aria-label="Next month"${shownMonth >= lastMonth ? " disabled" : ""}>›</button>
    </div>
    <div class="gl-cal-grid">${weekdays}${joinHtml(cells, "")}</div>
    ${hourList}`;
}

export function setGreatLakesHistoryTime(value) {
  const milliseconds = typeof value === "number" ? value : Date.parse(value);
  greatLakesHistoryMs = Number.isFinite(milliseconds) ? Math.round(milliseconds / HOUR_MS) * HOUR_MS : null;
  syncHistoryInput();
}

// The calendar's month and opened day while it is showing.
const historyCalendar = { open: false, month: null, day: null };

function historyLayerHours() {
  return historyHours(greatLakesHistoryIndex, greatLakesControlValue("layer"));
}

function renderHistoryCalendar() {
  const node = document.querySelector("[data-gl-history-calendar]");
  if (!node) return;
  node.hidden = !historyCalendar.open;
  document.querySelector("[data-gl-history-open]")?.setAttribute("aria-expanded", String(historyCalendar.open));
  if (historyCalendar.open) setHtml(node, historyCalendarHtml(historyLayerHours(), { month: historyCalendar.month, day: historyCalendar.day, selected: greatLakesHistoryMs }));
}

export function setHistoryCalendarOpen(open) {
  historyCalendar.open = open;
  if (open) {
    const hours = historyLayerHours();
    const anchor = Number.isFinite(greatLakesHistoryMs) ? greatLakesHistoryMs : hours[hours.length - 1];
    historyCalendar.month = Number.isFinite(anchor) ? monthStart(anchor) : null;
    historyCalendar.day = Number.isFinite(anchor) ? localDayKey(anchor) : null;
  }
  renderHistoryCalendar();
}

// The chosen time's label, the step buttons, and the calendar follow the saved times for the selected layer.
export function syncHistoryInput() {
  const hours = historyLayerHours();
  const label = document.querySelector("[data-gl-history-label]");
  const shown = Number.isFinite(greatLakesHistoryMs) ? historyButtonLabel(greatLakesHistoryMs) : "";
  if (label) label.textContent = shown || "Choose a time";
  document.querySelectorAll("[data-gl-history-step]").forEach((button) => {
    const step = Number(button.dataset.glHistoryStep);
    button.disabled = !Number.isFinite(greatLakesHistoryMs) || stepHistoryHour(hours, greatLakesHistoryMs, step) === greatLakesHistoryMs;
  });
  const open = document.querySelector("[data-gl-history-open]");
  if (open) {
    open.disabled = !hours.length;
    open.setAttribute("aria-label", shown ? `Showing ${shown}. Choose another saved date and time` : "Choose a saved date and time");
  }
  renderHistoryCalendar();
}

// "Mon, Oct 5 · 3 PM" on the calendar button.
export function historyButtonLabel(milliseconds) {
  return `${formatAppDate(milliseconds, { weekday: "short" })} · ${formatAppTime(milliseconds, { minute: undefined })}`;
}

export async function loadGreatLakesHistoryIndex({ fresh = false } = {}) {
  if (!fresh && greatLakesHistoryIndex && Date.now() - greatLakesHistoryIndexAt < HISTORY_INDEX_MAX_AGE_MS) return greatLakesHistoryIndex;
  try {
    greatLakesHistoryIndex = await window.noaaGreatLakesApi.history();
    greatLakesHistoryIndexAt = Date.now();
  } catch {
    greatLakesHistoryIndex ||= null;
  }
  return greatLakesHistoryIndex;
}

// Pick the newest saved sample on entering "Past 90 days", and snap to a saved time.
async function ensureGreatLakesHistoryTime(layer) {
  const index = await loadGreatLakesHistoryIndex();
  const hours = historyHours(index, layer);
  if (!hours.length) return false;
  const target = Number.isFinite(greatLakesHistoryMs) ? greatLakesHistoryMs : hours[hours.length - 1];
  greatLakesHistoryMs = nearestHistoryHour(hours, target);
  syncHistoryInput();
  return true;
}

function announceHistoryTime() {
  document.dispatchEvent(new CustomEvent("great-lakes-history-time", { detail: { time: greatLakesHistoryTime() } }));
}

// "Thursday, Oct 2 at 6 AM": a saved sample, always with its date.
function formatClockTime(date, { hour12 = false } = {}) {
  const options = appDateParts(date)?.minute
    ? {}
    : { minute: undefined };
  if (hour12) options.hour12 = true;
  return formatAppTime(date, options);
}

export function historyTimeLabel(value, options) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const day = formatAppDate(date, { weekday: "long" });
  const time = formatClockTime(date, options);
  return `${day} at ${time}`;
}

export function forecastChoiceLabel(hours) {
  if (hours === 0) return "Now";
  if (hours % 24 === 0) return `+${hours / 24} d`;
  return `+${hours} h`;
}

// Show the selected valid time without repeating its offset from now.
export function forecastTimeLabel(hours, now = new Date()) {
  const valid = new Date(now.getTime() + hours * 3600_000);
  const sameDay = appDayKey(valid) === appDayKey(now);
  const when = sameDay ? formatClockTime(valid) : historyTimeLabel(valid.toISOString());
  return hours === 0 ? `Now · ${when}` : when;
}

function forecastOptionsHtml() {
  const hours = Array.from({ length: GREAT_LAKES_MAX_FORECAST_HOUR + 1 }, (_, hour) => hour);
  return joinHtml([
    ...hours.map((hour) => html`<option value="${hour}">${hour === 0 ? "Now" : `${hour} hours`}</option>`),
    html`<option value="${HISTORY_FORECAST_VALUE}">Past 90 days</option>`
  ]);
}

function forecastChoiceHtml(value, label, checked) {
  return html`<label class="gl-forecast-choice"><input type="radio" name="gl-forecast-choice" value="${value}"${checked ? " checked" : ""} /><span>${label}</span></label>`;
}

function forecastChoicesHtml() {
  return joinHtml([
    ...GREAT_LAKES_FORECAST_OFFSETS.map((hours) => forecastChoiceHtml(String(hours), forecastChoiceLabel(hours), hours === 0)),
    forecastChoiceHtml(HISTORY_FORECAST_VALUE, "Past 90 days", false)
  ]);
}

export function greatLakesConditionsHtml() {
  return html`<section class="great-lakes-control" aria-label="Map layers">
    <label>Layer<select data-gl-layer>${layerOptionsHtml(savedLayer())}</select></label>
    <div class="great-lakes-air-options">
      <label>Air temperature coverage<select data-gl-air-coverage><option value="region">Region (land and lakes)</option><option value="lakes">Lakes only</option></select></label>
    </div>
    <div class="great-lakes-forecast-control">
      <span class="gl-forecast-title" id="glForecastTitle">Forecast</span>
      <select data-gl-forecast hidden aria-hidden="true" tabindex="-1">${forecastOptionsHtml()}</select>
      <div class="gl-forecast-slider">
        <input type="range" data-gl-forecast-slider min="0" max="${GREAT_LAKES_MAX_FORECAST_HOUR}" step="1" value="0" aria-labelledby="glForecastTitle" aria-valuetext="Now" />
        <output class="gl-forecast-time" data-gl-forecast-label aria-live="polite">Now</output>
      </div>
      <div class="gl-forecast-choices" data-gl-forecast-choices role="radiogroup" aria-labelledby="glForecastTitle">${forecastChoicesHtml()}</div>
    </div>
    ${greatLakesHistoryHtml()}
    ${greatLakesTimelineHtml()}
    <div class="gl-target-controls" hidden>
      <label>Target temperature <span data-gl-target-unit>°F</span><input type="number" data-gl-target-temperature min="32" max="86" step="0.5" value="48" aria-label="Target temperature" /></label>
      <small class="gl-target-temperature-range" data-gl-target-surface-range role="status" aria-live="polite">Loading Great Lakes surface temperatures…</small>
      <label>Matching depth<select data-gl-target-selection><option value="shallowest">Shallowest</option><option value="deepest">Deepest</option></select></label>
      <div class="gl-target-legend"><span data-gl-target-min>—</span><i></i><span data-gl-target-max>—</span></div>
      <small>Automatic depth range for matching water across available lakes. Clear water has no match; click water for all matching depths.</small>
    </div>
    <small class="gl-wind-status" data-gl-wind-status role="status" aria-live="polite"></small>
    <div class="great-lakes-wind-options">
      <label>Wind display<select data-gl-wind-display><option value="flow"${savedWindDisplay() === "flow" ? " selected" : ""}>Animated particles</option><option value="arrows"${savedWindDisplay() === "arrows" ? " selected" : ""}>Static arrows</option><option value="off"${savedWindDisplay() === "off" ? " selected" : ""}>None</option></select></label>
      <label class="great-lakes-wind-particle-color">Particle colour<select data-gl-wind-color aria-label="Wind particle colour">${windParticleColorOptionsHtml(savedWindParticleColor())}</select></label>
    </div>
    <label class="great-lakes-depth-control">Depth <output data-gl-depth-label>Surface</output><input data-gl-depth type="range" min="0" max="${GREAT_LAKES_DEPTH_SLIDER_MAX}" step="1" value="0" aria-label="NOAA model depth level" aria-valuetext="Surface" /></label>
    <div class="great-lakes-wave-options">
      <label>Wave display<select data-gl-wave-display><option value="flow"${savedWaveDisplay() === "flow" ? " selected" : ""}>Animated flow</option><option value="arrows"${savedWaveDisplay() === "arrows" ? " selected" : ""}>Static arrows</option><option value="off"${savedWaveDisplay() === "off" ? " selected" : ""}>Off</option></select></label>
      <label class="great-lakes-wave-particle-color">Wave particle colour<select data-gl-wave-color aria-label="Wave particle colour">${waveParticleColorOptionsHtml(savedWaveParticleColor())}</select></label>
    </div>
    <div class="great-lakes-current-options">
      <label>Background<select data-gl-current-background>${currentBackgroundOptionsHtml(savedCurrentBackground())}</select></label>
      <label>Current display<select data-gl-current-display><option value="flow">Animated flow</option><option value="arrows">Static arrows</option></select></label>
      <label class="great-lakes-flow-color">Flow colour<select data-gl-flow-color aria-label="Current flow colour">${flowColorOptionsHtml(savedFlowColor())}</select></label>
      <label>Particle density<select data-gl-density><option value="low">Low</option><option value="medium" selected>Medium</option><option value="high">High</option><option value="very-high">Very High</option></select></label>
      <label>Animation speed<select data-gl-animation-speed><option value="slow">Slow</option><option value="normal" selected>Normal</option><option value="fast">Fast</option></select></label>
    </div>
    <div class="great-lakes-current-legend"><span data-gl-current-min>—</span><i></i><span data-gl-current-max>—</span></div>
    <p data-gl-status>Loading NOAA forecast…</p>
    <div class="great-lakes-legend"><span data-gl-temperature-min>—</span><i></i><span data-gl-temperature-max>—</span></div>
    <label class="great-lakes-color-mode" title="Fit colour scale to visible temperatures"><input type="checkbox" data-temperature-colors aria-label="Fit colour scale to visible temperatures"${savedTemperatureColorMode() === "dynamic" ? " checked" : ""}> Fit colour scale to visible temperatures</label>
    <div class="great-lakes-thermocline-legend"><span data-gl-thermocline-min>—</span><i></i><span data-gl-thermocline-max>—</span></div>
    <div class="great-lakes-thermocline-mixed"><b aria-hidden="true"></b>Clear: no thermocline</div>
    <div class="great-lakes-wave-legend"><span data-gl-wave-min>—</span><i></i><span data-gl-wave-max>—</span></div>
    <div class="great-lakes-wind-legend"><span data-gl-wind-min>—</span><i></i><span data-gl-wind-max>—</span></div>
    <div class="great-lakes-upwelling-legend"><span>Upwelling</span><i></i><span>Downwelling</span></div>
  </section>`;
}

export function ensureGreatLakesLoadingIndicator() {
  const mapNode = document.querySelector("#map");
  if (!mapNode || mapNode.querySelector("[data-gl-map-loading]")) return;
  insertHtml(mapNode, "beforeend", html`<div class="great-lakes-map-loading" data-gl-map-loading role="status" aria-live="polite" aria-hidden="true"><div class="great-lakes-map-loading-card"><span class="great-lakes-map-spinner" aria-hidden="true"></span><span data-gl-map-loading-text>Loading NOAA forecast…</span></div></div>`);
}

// A card in the middle of the map while data loads; it shows the same text as
// the panel's status line ("Loading the forecast animation… 40%").
export function setGreatLakesMapLoading(isLoading, message = "Loading NOAA forecast…") {
  setMapLoadingReason("layer", isLoading, message);
}

// Everything the map is waiting for (lake data, the first map tiles), with the
// message for each; the card stays up until all of them are done.
const mapLoadingReasons = new Map();
let mapReadyAnnounced = false;

export function setMapLoadingReason(reason, isLoading, message) {
  ensureGreatLakesLoadingIndicator();
  const mapNode = document.querySelector("#map");
  const indicator = mapNode?.querySelector("[data-gl-map-loading]");
  const text = indicator?.querySelector("[data-gl-map-loading-text]");
  const started = isLoading && !mapLoadingReasons.has(reason);
  if (isLoading) { if (started) mapLoadingReasons.set(reason, message); }
  else mapLoadingReasons.delete(reason);
  const wasBusy = mapNode?.getAttribute("aria-busy") === "true";
  const busy = mapLoadingReasons.size > 0;
  if (text && started) text.textContent = message;
  else if (text && !isLoading && busy) text.textContent = [...mapLoadingReasons.values()].at(-1);
  mapNode?.classList.toggle("is-great-lakes-loading", busy);
  mapNode?.setAttribute("aria-busy", String(busy));
  indicator?.setAttribute("aria-hidden", String(!busy));
  if (wasBusy && !busy && !mapReadyAnnounced) {
    mapReadyAnnounced = true;
    document.dispatchEvent(new CustomEvent("great-lakes-map-ready"));
  }
}

export function greatLakesDepthLabel(meters) {
  if (!Number.isFinite(Number(meters)) || Number(meters) <= 0.25) return "Surface";
  return formatUnitValue(meters, "depth", "m", { decimals: 0 });
}

export function greatLakesDepthSliderLabel(value) {
  const position = Math.round(Math.max(0, Math.min(GREAT_LAKES_DEPTH_SLIDER_MAX, Number(value) || 0)));
  const feet = GREAT_LAKES_DEPTH_LEVELS_FEET[position];
  return feet ? `${feet} ft` : "Surface";
}

export function greatLakesDepthValueLabel(meters) {
  if (!Number.isFinite(Number(meters))) return "—";
  return formatUnitValue(meters, "depth", "m", { decimals: 0 });
}

export function greatLakesDepthFromSlider(value) {
  const position = Math.round(Math.max(0, Math.min(GREAT_LAKES_DEPTH_SLIDER_MAX, Number(value) || 0)));
  return GREAT_LAKES_DEPTH_LEVELS_FEET[position] * 0.3048;
}

export function waterTemperatureLabel(temperatureC) {
  return formatUnitValue(temperatureC, "waterTemperature", "C", { decimals: 1 });
}

export function airTemperatureLabel(temperatureC) {
  return formatUnitValue(temperatureC, "airTemperature", "C", { decimals: 1 });
}

export function thermoclineGradientLabel(gradientCPerMeter) {
  const temperatureUnit = unitPreference("waterTemperature");
  const depthUnit = unitPreference("depth");
  const temperatureFactor = temperatureUnit === "F" ? 1.8 : 1;
  const depthFactor = convertUnitValue(1, "m", depthUnit) || 1;
  return `${(gradientCPerMeter * temperatureFactor / depthFactor).toFixed(2)} °${temperatureUnit}/${depthUnit}`;
}

export function greatLakesDepthAxisUnit() {
  return greatLakesDepthLabel(1).match(/(fm|ft|m)$/i)?.[1] || "m";
}

export function greatLakesDepthAxisTick(depth) {
  if (depth <= 0.25) return "Surface";
  return greatLakesDepthLabel(depth).replace(/\s*(?:fm|ft|m)$/i, "");
}

export function ensureGreatLakesConditions(map) {
  if (greatLakesConditionsControl) return;
  greatLakesConditionsLayer = L.layerGroup().addTo(map);
  ensureGreatLakesLoadingIndicator();
  const host = document.querySelector("#greatLakesConditions");
  if (!host) return;
  setHtml(host, greatLakesConditionsHtml());
  const initialLayer = host.querySelector("[data-gl-layer]")?.value;
  if (initialLayer) {
    document.dispatchEvent(new CustomEvent("great-lakes-layer-change", {
      detail: { layer: initialLayer, initial: true }
    }));
  }
  applyFlowColor(savedFlowColor());
  const syncLayerSpecificControls = () => syncGreatLakesLayerControls(host);
  syncLayerSpecificControls();
  syncTimelineControls();
  host.querySelectorAll("select").forEach((select) => select.addEventListener("change", () => {
    if (select.matches("[data-gl-flow-color]")) {
      onFlowColorChange(map, host, select.value);
      return;
    }
    if (select.matches("[data-gl-wind-color]")) {
      setWindParticleColor(select.value);
      return;
    }
    if (select.matches("[data-gl-wave-color]")) {
      setWaveParticleColor(select.value);
      return;
    }
    if (select.matches("[data-gl-layer], [data-gl-current-background], [data-gl-forecast], [data-gl-wave-display], [data-gl-wind-display]")) syncLayerSpecificControls();
    if (select.matches("[data-gl-current-background]")) storeCurrentBackground(select.value);
    if (select.matches("[data-gl-layer]")) {
      syncTimelineControls();
      try { localStorage.setItem(LAYER_STORAGE_KEY, select.value); } catch { /* storage unavailable */ }
      document.dispatchEvent(new CustomEvent("great-lakes-layer-change", { detail: { layer: select.value } }));
    }
    if (select.matches("[data-gl-wave-display]")) {
      try { localStorage.setItem(WAVE_DISPLAY_STORAGE_KEY, select.value); } catch { /* storage unavailable */ }
    }
    if (select.matches("[data-gl-wind-display]")) {
      try { localStorage.setItem(WIND_DISPLAY_STORAGE_KEY, select.value); } catch { /* storage unavailable */ }
    }
    // Picking a forecast time ends the animation; other choices change what it animates.
    if (select.matches("[data-gl-forecast]")) {
      stopGreatLakesAnimation();
      syncLayerSpecificControls();
      document.dispatchEvent(new CustomEvent("great-lakes-forecast-change", {
        detail: select.value === HISTORY_FORECAST_VALUE
          ? { mode: "history" }
          : { mode: "forecast", forecastHour: Number(select.value) || 0 }
      }));
    }
    loadGreatLakesConditions(map);
  }));
  // The segmented buttons drive the hidden select, so every existing forecast
  // reader (shared links, the tour, the animation) keeps working unchanged.
  setupGreatLakesForecastChoices(host);
  const depthSlider = host.querySelector("[data-gl-depth]");
  const syncDepthLabel = () => {
    const label = greatLakesDepthSliderLabel(depthSlider.value);
    host.querySelector("[data-gl-depth-label]").textContent = label;
    depthSlider.setAttribute("aria-valuetext", label);
  };
  depthSlider.addEventListener("input", syncDepthLabel);
  depthSlider.addEventListener("change", () => {
    document.dispatchEvent(new CustomEvent("great-lakes-depth-change", {
      detail: { depthMeters: greatLakesDepthFromSlider(depthSlider.value) }
    }));
    loadGreatLakesConditions(map);
  });
  setupGreatLakesTimeline(host, () => loadGreatLakesConditions(map));
  setupGreatLakesHistoryControls(host, map);
  setupWind(map, () => ({ hour: Number(greatLakesControlValue("forecast")) || 0, history: greatLakesHistoryMode(), time: greatLakesHistoryTime(), animation: greatLakesAnimationActive() }));
  document.addEventListener("great-lakes-wave-color-changed", (event) => {
    greatLakesParticleLayer?.setParticleColor(waveParticleColorParts(event.detail?.color));
  });
  const targetInput = host.querySelector("[data-gl-target-temperature]");
  syncTargetTemperatureUnits();
  targetInput.addEventListener("change", () => {
    if (!targetInput.checkValidity() || targetInput.value === "") { targetInput.reportValidity(); return; }
    targetTemperatureC = unitPreference("waterTemperature") === "F" ? (Number(targetInput.value) - 32) / 1.8 : Number(targetInput.value);
    loadGreatLakesConditions(map);
  });
  document.addEventListener("glc-units-changed", syncTargetTemperatureUnits);
  greatLakesConditionsControl = host;
  // Layers always cover every lake at full detail, so panning and zooming
  // never request (or wait for) a different drawing.
  if (map._loaded) loadGreatLakesConditions(map);
  else map.once("load", () => loadGreatLakesConditions(map));
  setInterval(() => refreshGreatLakesIfStale(map), GREAT_LAKES_STATUS_POLL_MS);
  document.addEventListener("visibilitychange", () => { if (!document.hidden) refreshGreatLakesIfStale(map); });
}

// The step buttons and calendar of "Past 90 days".
export function setupGreatLakesHistoryControls(host, map) {
  const show = (milliseconds) => {
    if (!Number.isFinite(milliseconds) || milliseconds === greatLakesHistoryMs) {
      syncHistoryInput();
      return;
    }
    greatLakesHistoryMs = milliseconds;
    syncHistoryInput();
    loadGreatLakesConditions(map);
  };
  host.querySelectorAll("[data-gl-history-step]").forEach((button) => button.addEventListener("click", () => {
    show(stepHistoryHour(historyLayerHours(), greatLakesHistoryMs, Number(button.dataset.glHistoryStep)));
  }));
  host.querySelector("[data-gl-history-open]")?.addEventListener("click", () => setHistoryCalendarOpen(!historyCalendar.open));
  const calendar = host.querySelector("[data-gl-history-calendar]");
  calendar?.addEventListener("click", (event) => {
    const month = event.target.closest("[data-gl-cal-month]");
    const day = event.target.closest("[data-gl-cal-day]");
    const hour = event.target.closest("[data-gl-cal-hour]");
    if (month && !month.disabled) {
      historyCalendar.month = shiftMonth(historyCalendar.month, Number(month.dataset.glCalMonth));
      historyCalendar.day = null;
      renderHistoryCalendar();
    } else if (day) {
      historyCalendar.day = day.dataset.glCalDay;
      renderHistoryCalendar();
      calendar.querySelector(".gl-cal-hour.is-selected, .gl-cal-hour")?.focus();
    } else if (hour) {
      setHistoryCalendarOpen(false);
      host.querySelector("[data-gl-history-open]")?.focus();
      show(Number(hour.dataset.glCalHour));
    }
  });
  host.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && historyCalendar.open) {
      setHistoryCalendarOpen(false);
      host.querySelector("[data-gl-history-open]")?.focus();
    }
  });
  document.addEventListener("click", (event) => {
    // The path is taken when the click happens: a day button re-renders the calendar under it.
    const inside = event.composedPath().some((node) => node instanceof Element && node.matches("[data-gl-history-controls]"));
    if (historyCalendar.open && !inside) setHistoryCalendarOpen(false);
  });
}

// Point the preset buttons and the slider at the hidden select that holds the
// canonical value, so either one reaches every existing forecast listener.
export function setupGreatLakesForecastChoices(host) {
  const forecast = host?.querySelector("[data-gl-forecast]");
  if (!forecast) return;
  host.querySelectorAll("[data-gl-forecast-choices] input").forEach((choice) => {
    choice.addEventListener("change", () => {
      if (!choice.checked) return;
      forecast.value = choice.value;
      forecast.dispatchEvent(new Event("change", { bubbles: true }));
    });
  });
  const slider = host.querySelector("[data-gl-forecast-slider]");
  if (!slider) return;
  // Dragging only moves the label; the map is asked for a new hour on release.
  slider.addEventListener("input", () => syncGreatLakesForecastLabel(host));
  slider.addEventListener("change", () => {
    forecast.value = slider.value;
    forecast.dispatchEvent(new Event("change", { bubbles: true }));
  });
}

// Keep the slider, its label, and the preset buttons showing whatever the
// hidden select currently holds.
export function syncGreatLakesForecastLabel(host = document.querySelector("#greatLakesConditions")) {
  const slider = host?.querySelector("[data-gl-forecast-slider]");
  const label = host?.querySelector("[data-gl-forecast-label]");
  if (!slider || !label) return;
  const history = host.querySelector("[data-gl-forecast]")?.value === HISTORY_FORECAST_VALUE;
  const text = history ? "Past 90 days" : forecastTimeLabel(Number(slider.value) || 0);
  label.textContent = text;
  slider.setAttribute("aria-valuetext", text);
}

export function syncGreatLakesLayerControls(host = document.querySelector("#greatLakesConditions")) {
  if (!host) return;
  const layer = host.querySelector("[data-gl-layer]")?.value;
  const forecastOnly = ["target-temperature", "wind"].includes(layer);
  const forecast = host.querySelector("[data-gl-forecast]");
  const noHistory = layer === "target-temperature";
  if (noHistory && forecast?.value === HISTORY_FORECAST_VALUE) forecast.value = "0";
  const historyOption = forecast?.querySelector(`option[value="${HISTORY_FORECAST_VALUE}"]`);
  if (historyOption) historyOption.disabled = noHistory;
  const maxHour = greatLakesMaxForecastHour(layer);
  forecast?.querySelectorAll("option").forEach((option) => {
    if (option.value !== HISTORY_FORECAST_VALUE) option.disabled = Number(option.value) > maxHour;
  });
  const history = forecast?.value === HISTORY_FORECAST_VALUE;
  // The wave model only reaches 48 hours: pull a longer selection back rather
  // than asking for an hour NOAA cannot draw.
  if (forecast && !history && Number(forecast.value) > maxHour) forecast.value = String(maxHour);
  const slider = host.querySelector("[data-gl-forecast-slider]");
  if (slider) {
    slider.max = String(maxHour);
    slider.disabled = history;
    if (!history) slider.value = forecast?.value ?? "0";
  }
  host.querySelectorAll("[data-gl-forecast-choices] input").forEach((choice) => {
    choice.disabled = choice.value === HISTORY_FORECAST_VALUE ? noHistory : Number(choice.value) > maxHour;
    choice.checked = choice.value === forecast?.value;
  });
  syncGreatLakesForecastLabel(host);
  const targetControls = host.querySelector(".gl-target-controls");
  if (targetControls) targetControls.hidden = layer !== "target-temperature";
  host.classList.toggle("has-forecast-only-layer", forecastOnly);
  host.classList.toggle("has-wind-layer", layer === "wind");
  host.classList.toggle("has-wind-flow", layer === "wind" && host.querySelector("[data-gl-wind-display]")?.value === "flow");
  host.classList.toggle("has-history", host.querySelector("[data-gl-forecast]")?.value === HISTORY_FORECAST_VALUE);
  host.classList.toggle("has-current-layer", layer === "currents");
  host.classList.toggle("has-temperature-layer", ["temperature", "bottom-temperature", "air-temperature"].includes(layer));
  host.classList.toggle("has-bottom-temperature-layer", layer === "bottom-temperature");
  host.classList.toggle("has-air-temperature-layer", layer === "air-temperature");
  host.classList.toggle("has-thermocline-layer", layer === "thermocline");
  host.classList.toggle("has-wave-layer", layer === "waves");
  host.classList.toggle("has-wave-flow", layer === "waves" && host.querySelector("[data-gl-wave-display]")?.value === "flow");
  host.classList.toggle("has-upwelling-layer", layer === "upwelling");
  const background = host.querySelector("[data-gl-current-background]")?.value;
  host.classList.toggle("has-temperature-background", layer === "currents" && background === "temperature");
  host.classList.toggle("has-no-background", layer === "currents" && background === "none");
  host.classList.toggle("has-speed-flow", layer === "currents" && host.querySelector("[data-gl-flow-color]")?.value === FLOW_COLOR_BY_SPEED);
}

export function savedWaveDisplay() {
  try {
    const saved = localStorage.getItem(WAVE_DISPLAY_STORAGE_KEY);
    return ["flow", "arrows", "off"].includes(saved) ? saved : "flow";
  } catch { return "flow"; }
}

export function savedWindDisplay() {
  try {
    const saved = localStorage.getItem(WIND_DISPLAY_STORAGE_KEY);
    return ["flow", "arrows", "off"].includes(saved) ? saved : "flow";
  } catch { return "flow"; }
}

export function waveHeightLabel(meters) {
  if (!Number.isFinite(Number(meters))) return "—";
  return formatUnitValue(Number(meters), "waveHeight", "m", { decimals: 1 });
}

// The same chop categories the trip editor uses (Settings → chop ranges).
export function waveChopLabel(meters) {
  const feet = convertUnitValue(Number(meters), "m", "ft");
  if (!Number.isFinite(feet)) return "";
  const ranges = currentChopRanges();
  const bounded = ranges.find((range) => range.maxFeet !== null && feet <= Number(range.maxFeet));
  return (bounded || ranges.find((range) => range.maxFeet === null) || ranges.at(-1))?.label || "";
}

// NOAA reports where waves come *from*; arrows point the way they travel.
export function waveTravelDegrees(fromDegrees) {
  return ((Number(fromDegrees) + 180) % 360 + 360) % 360;
}

export function waveDirectionText(fromDegrees) {
  if (!Number.isFinite(Number(fromDegrees))) return "";
  const degrees = Math.round(Number(fromDegrees)) % 360;
  return `From ${currentDirectionLabel(degrees)} (${degrees}°)`;
}

export function savedCurrentBackground() {
  try {
    const saved = localStorage.getItem(CURRENT_BACKGROUND_STORAGE_KEY);
    return CURRENT_BACKGROUND_OPTIONS.some(([value]) => value === saved) ? saved : "speed";
  } catch { return "speed"; }
}

export function storeCurrentBackground(value) {
  try { localStorage.setItem(CURRENT_BACKGROUND_STORAGE_KEY, value); } catch { /* storage unavailable */ }
}

export function currentBackgroundOptionsHtml(selected) {
  return joinHtml(CURRENT_BACKGROUND_OPTIONS.map(([value, label]) => html`<option value="${value}"${value === selected ? " selected" : ""}>${label}</option>`), "");
}

export function flowColorOptionsHtml(selected) {
  return joinHtml(FLOW_COLOR_OPTIONS.map(([value, label]) => html`<option value="${value}"${value === selected ? " selected" : ""}>${label}</option>`), "");
}

export function windParticleColorOptionsHtml(selected) {
  return joinHtml(WIND_PARTICLE_COLOR_OPTIONS.map(([value, label]) => html`<option value="${value}"${value === selected ? " selected" : ""}>${label}</option>`), "");
}

export function waveParticleColorOptionsHtml(selected) {
  return joinHtml(WAVE_PARTICLE_COLOR_OPTIONS.map(([value, label]) => html`<option value="${value}"${value === selected ? " selected" : ""}>${label}</option>`), "");
}

export function normalizedWindParticleColor(value) {
  const text = String(value || "").toLowerCase();
  return WIND_PARTICLE_COLOR_OPTIONS.some(([option]) => option === text) ? text : DEFAULT_WIND_PARTICLE_COLOR;
}

export function savedWindParticleColor() {
  try { return normalizedWindParticleColor(localStorage.getItem(WIND_PARTICLE_COLOR_STORAGE_KEY)); } catch { return DEFAULT_WIND_PARTICLE_COLOR; }
}

export function windParticleColorParts(color = savedWindParticleColor()) {
  return particleColorParts(normalizedWindParticleColor(color));
}

export function normalizedWaveParticleColor(value) {
  const text = String(value || "").toLowerCase();
  return WAVE_PARTICLE_COLOR_OPTIONS.some(([option]) => option === text) ? text : DEFAULT_WAVE_PARTICLE_COLOR;
}

export function savedWaveParticleColor() {
  try { return normalizedWaveParticleColor(localStorage.getItem(WAVE_PARTICLE_COLOR_STORAGE_KEY)); } catch { return DEFAULT_WAVE_PARTICLE_COLOR; }
}

export function waveParticleColorParts(color = savedWaveParticleColor()) {
  return particleColorParts(normalizedWaveParticleColor(color));
}

function particleColorParts(value) {
  const [red, green, blue] = [1, 3, 5].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16));
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  return { rgb: `${red}, ${green}, ${blue}`, halo: luminance > 0.5 ? "6, 16, 34" : "255, 255, 255" };
}

export function setWindParticleColor(color) {
  const value = normalizedWindParticleColor(color);
  try { localStorage.setItem(WIND_PARTICLE_COLOR_STORAGE_KEY, value); } catch { /* storage unavailable */ }
  document.dispatchEvent(new CustomEvent("great-lakes-wind-color-changed", { detail: { color: value } }));
  return value;
}

export function setWaveParticleColor(color) {
  const value = normalizedWaveParticleColor(color);
  try { localStorage.setItem(WAVE_PARTICLE_COLOR_STORAGE_KEY, value); } catch { /* storage unavailable */ }
  document.dispatchEvent(new CustomEvent("great-lakes-wave-color-changed", { detail: { color: value } }));
  return value;
}

// Only the listed colours are offered; anything else (including colours saved
// by the earlier free colour picker) falls back to white.
export function normalizedFlowColor(value) {
  const text = String(value || "").toLowerCase();
  return FLOW_COLOR_OPTIONS.some(([option]) => option === text) ? text : DEFAULT_FLOW_COLOR;
}

export function savedFlowColor() {
  try { return normalizedFlowColor(localStorage.getItem(FLOW_COLOR_STORAGE_KEY)); } catch { return DEFAULT_FLOW_COLOR; }
}

// The trail keeps a contrasting outline so every colour stays readable over
// both the light basemap and the bright temperature colours. Speed colours
// get a dark outline.
export function flowColorParts(color) {
  const value = normalizedFlowColor(color);
  if (value === FLOW_COLOR_BY_SPEED) return { rgb: "255, 255, 255", halo: "6, 16, 34", bySpeed: true };
  const [red, green, blue] = [1, 3, 5].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16));
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  return { rgb: `${red}, ${green}, ${blue}`, halo: luminance > 0.5 ? "6, 16, 34" : "255, 255, 255", bySpeed: false };
}

export function applyFlowColor(color) {
  greatLakesFlowColor = normalizedFlowColor(color);
  const { rgb, halo } = flowColorParts(greatLakesFlowColor);
  const mapNode = document.querySelector("#map");
  mapNode?.style.setProperty("--gl-flow-color", `rgb(${rgb})`);
  mapNode?.style.setProperty("--gl-flow-halo", `rgba(${halo}, 0.72)`);
}

// Animated flow reads the colour every frame, so a solid colour applies
// without reloading. Speed colours on top of speed shading would vanish into
// the same palette, so choosing them switches the background to the map.
export function onFlowColorChange(map, host, value) {
  const previous = greatLakesFlowColor;
  applyFlowColor(value);
  try { localStorage.setItem(FLOW_COLOR_STORAGE_KEY, greatLakesFlowColor); } catch { /* storage unavailable */ }
  const background = host.querySelector("[data-gl-current-background]");
  let reload = greatLakesControlValue("current-display") === "arrows" && (previous === FLOW_COLOR_BY_SPEED) !== (greatLakesFlowColor === FLOW_COLOR_BY_SPEED);
  if (greatLakesFlowColor === FLOW_COLOR_BY_SPEED && background?.value === "speed") {
    background.value = "none";
    storeCurrentBackground("none");
    reload = true;
  }
  syncGreatLakesLayerControls(host);
  if (reload) loadGreatLakesConditions(map);
}

export function renderGreatLakesConditionsHost() {
  const host = document.querySelector("#greatLakesConditions");
  if (host && !host.querySelector("[data-gl-layer]")) {
    setHtml(host, greatLakesConditionsHtml());
    syncGreatLakesLayerControls(host);
  }
  return host;
}

export function greatLakesModelsForView(map) {
  const view = map.getBounds();
  return Object.entries(GREAT_LAKES_MODEL_BOUNDS)
    .filter(([, [[south, west], [north, east]]]) => view.getNorth() >= south && view.getSouth() <= north && view.getEast() >= west && view.getWest() <= east)
    .map(([model]) => model);
}

export function greatLakesControlValue(name) {
  const control = document.querySelector(`[data-gl-${name}]`);
  if (name === "depth" && control) return String(greatLakesDepthFromSlider(control.value));
  return control ? control.value : (name === "layer" ? "" : "0");
}

// The time the map shows: the animation frame on screen, otherwise the forecast choice ("Now" for a past hour; see greatLakesTimeParams).
export function greatLakesForecastHour() {
  if (greatLakesHistoryMode()) return "0";
  return greatLakesAnimationForecastHour() ?? greatLakesControlValue("forecast");
}

// Saved past maps are surface only.
export function greatLakesShownDepth() {
  if (["bottom-temperature", "air-temperature"].includes(greatLakesControlValue("layer"))) return "0";
  return greatLakesHistoryMode() ? "0" : greatLakesControlValue("depth");
}

export function setGreatLakesStatus(message, error = false) {
  const loadingText = document.querySelector("[data-gl-map-loading-text]");
  if (loadingText && !error) loadingText.textContent = message;
  const node = document.querySelector("[data-gl-status]");
  if (node) {
    node.textContent = message;
    node.classList.toggle("is-error", error);
  }
}

export function setThermoclineLegendRange(metadata = {}) {
  const minimum = document.querySelector("[data-gl-thermocline-min]");
  const maximum = document.querySelector("[data-gl-thermocline-max]");
  if (minimum) minimum.textContent = greatLakesDepthValueLabel(Number(metadata.minDepthMeters));
  if (maximum) maximum.textContent = greatLakesDepthValueLabel(Number(metadata.maxDepthMeters));
}

export function setTargetTemperatureLegendRange(metadata = null) {
  const minimum = document.querySelector("[data-gl-target-min]");
  const maximum = document.querySelector("[data-gl-target-max]");
  const low = Number(metadata?.minDepthMeters);
  const high = Number(metadata?.maxDepthMeters);
  targetDepthBounds = metadata?.minDepthMeters != null && metadata?.maxDepthMeters != null && Number.isFinite(low) && Number.isFinite(high)
    ? { minDepthMeters: low, maxDepthMeters: high }
    : null;
  if (minimum) minimum.textContent = targetDepthBounds ? greatLakesDepthValueLabel(low) : "—";
  if (maximum) maximum.textContent = targetDepthBounds ? greatLakesDepthValueLabel(high) : "—";
}

function renderTargetTemperatureSurfaceRange() {
  const range = document.querySelector("[data-gl-target-surface-range]");
  if (!range) return;
  if (targetSurfaceTemperatureBounds) {
    range.textContent = "Great Lakes surface: min " + waterTemperatureLabel(targetSurfaceTemperatureBounds.minC)
      + " · max " + waterTemperatureLabel(targetSurfaceTemperatureBounds.maxC);
  } else if (targetSurfaceTemperatureRangeState === "loading") {
    range.textContent = "Loading Great Lakes surface temperatures…";
  } else {
    range.textContent = "Great Lakes surface temperatures unavailable.";
  }
}

export function setTargetTemperatureSurfaceRange(metadata = null, state = "ready") {
  const low = Number(metadata?.minSurfaceTemperatureC);
  const high = Number(metadata?.maxSurfaceTemperatureC);
  targetSurfaceTemperatureBounds = metadata?.minSurfaceTemperatureC != null
    && metadata?.maxSurfaceTemperatureC != null
    && Number.isFinite(low) && Number.isFinite(high)
    ? { minC: low, maxC: high }
    : null;
  targetSurfaceTemperatureRangeState = targetSurfaceTemperatureBounds ? "ready" : state;
  renderTargetTemperatureSurfaceRange();
}

export function setTemperatureLegendRange(metadata = {}, unit = "waterTemperature") {
  const minimum = document.querySelector("[data-gl-temperature-min]");
  const maximum = document.querySelector("[data-gl-temperature-max]");
  const parsedLow = Number(metadata.minC), parsedHigh = Number(metadata.maxC);
  const low = metadata.minC != null && Number.isFinite(parsedLow) ? parsedLow : TEMPERATURE_COLOR_RANGE_C[0];
  const high = metadata.maxC != null && Number.isFinite(parsedHigh) ? parsedHigh : TEMPERATURE_COLOR_RANGE_C[1];
  const label = unit === "airTemperature" ? airTemperatureLabel : waterTemperatureLabel;
  if (minimum) minimum.textContent = label(low);
  if (maximum) maximum.textContent = label(high);
}

export function setCurrentLegendRange(metadata = {}) {
  const minimum = document.querySelector("[data-gl-current-min]");
  const maximum = document.querySelector("[data-gl-current-max]");
  if (minimum) minimum.textContent = currentSpeedLabel(Number(metadata.minSpeedMetersPerSecond));
  if (maximum) maximum.textContent = currentSpeedLabel(Number(metadata.maxSpeedMetersPerSecond));
}

export function setWaveLegendRange(metadata = {}) {
  const minimum = document.querySelector("[data-gl-wave-min]");
  const maximum = document.querySelector("[data-gl-wave-max]");
  const bar = document.querySelector(".great-lakes-wave-legend i");
  const high = Number(metadata.maxHeightMeters) > 0 ? Number(metadata.maxHeightMeters) : WAVE_DEFAULT_MAX_METERS;
  if (minimum) minimum.textContent = Number(metadata.minHeightMeters) > 0 ? waveHeightLabel(metadata.minHeightMeters) : "Calm";
  if (maximum) maximum.textContent = waveHeightLabel(high);
  if (bar) bar.style.background = paletteGradient(waveStops(high));
}

export function setWindLegendRange(metadata = {}) {
  const minimum = document.querySelector("[data-gl-wind-min]");
  const maximum = document.querySelector("[data-gl-wind-max]");
  const bar = document.querySelector(".great-lakes-wind-legend i");
  const high = Number(metadata.maxSpeedMetersPerSecond) > 0 ? Number(metadata.maxSpeedMetersPerSecond) : WIND_DEFAULT_MAX_METERS_PER_SECOND;
  if (minimum) minimum.textContent = Number(metadata.minSpeedMetersPerSecond) > 0 ? windSpeedLabel(metadata.minSpeedMetersPerSecond) : "Calm";
  if (maximum) maximum.textContent = windSpeedLabel(high);
  if (bar) bar.style.background = paletteGradient(windStops(high));
}

// The layer's colours follow what is on screen (great-lakes-palette.js); null without a layer.
export let greatLakesPaletteFit = null;

// Legends, flow colours, and station dots follow the colour range on screen.
export function showPaletteRange(kind, low, high) {
  if (kind === "temperature" || kind === "air-temperature") {
    setTemperatureLegendRange({ minC: low, maxC: high }, kind === "air-temperature" ? "airTemperature" : "waterTemperature");
  }
  else if (kind === "thermocline") setThermoclineLegendRange({ minDepthMeters: low, maxDepthMeters: high });
  else if (kind === "waves") setWaveLegendRange({ minHeightMeters: low, maxHeightMeters: high });
  else if (kind === "wind") setWindLegendRange({ minSpeedMetersPerSecond: low, maxSpeedMetersPerSecond: high });
  else if (kind === "upwelling") { /* fixed colours: the legend never changes */ }
  else {
    setCurrentLegendRange({ minSpeedMetersPerSecond: low, maxSpeedMetersPerSecond: high });
    setCurrentSpeedMax({ maxSpeedMetersPerSecond: high });
  }
  const readingKind = kind === "temperature" && greatLakesControlValue("layer") === "bottom-temperature" ? "bottom-temperature" : kind;
  document.dispatchEvent(new CustomEvent("great-lakes-palette-range", { detail: { kind: readingKind, minimum: low, maximum: high, depth: Number(greatLakesShownDepth()) || 0 } }));
}

export function stopPaletteFit() {
  greatLakesPaletteFit?.stop();
  greatLakesPaletteFit = null;
}

// A layer's lake images, coloured to fit what is on screen.
function drawPaletteRasters(rasters, kind, options) {
  stopPaletteFit();
  const filter = createPaletteFilter({ smooth: ["temperature", "air-temperature", "waves", "wind"].includes(kind) });
  paletteOverlays(rasters, filter, { ...options, interactive: false }).forEach((overlay) => overlay.addTo(greatLakesConditionsLayer));
  greatLakesPaletteFit = fitPaletteToView(greatLakesConditionsLayer._map, [rasters], kind, filter, (low, high) => showPaletteRange(kind, low, high));
}

export function renderGreatLakesWaveRasters(rasters, loadRevision) {
  if (loadRevision !== greatLakesLoadRevision || greatLakesControlValue("layer") !== "waves") return;
  drawPaletteRasters(rasters, "waves", { opacity: 0.86, className: "great-lakes-wave-raster" });
}

// About one arrow per this many screen pixels, whatever the zoom.
export const WAVE_ARROW_SPACING_PX = 54;
export const WIND_ARROW_SPACING_PX = WAVE_ARROW_SPACING_PX;

// Keep the arrow nearest each screen cell's centre so spacing stays even.
function thinDirectionArrows(arrows, project, bounds, spacing) {
  const chosen = new Map();
  arrows.forEach((arrow) => {
    if (bounds && !bounds.contains([arrow.latitude, arrow.longitude])) return;
    const point = project(arrow);
    const column = Math.floor(point.x / spacing), row = Math.floor(point.y / spacing);
    const key = `${column}:${row}`;
    const offset = Math.hypot(point.x - (column + 0.5) * spacing, point.y - (row + 0.5) * spacing);
    const existing = chosen.get(key);
    if (!existing || offset < existing.offset) chosen.set(key, { arrow, offset });
  });
  return [...chosen.values()].map((item) => item.arrow);
}

export function thinWaveArrows(arrows, project, bounds, spacing = WAVE_ARROW_SPACING_PX) {
  return thinDirectionArrows(arrows, project, bounds, spacing);
}

export function thinWindArrows(points, project, bounds, spacing = WIND_ARROW_SPACING_PX) {
  return thinDirectionArrows(points, project, bounds, spacing);
}

export function createWindArrowLayer(map, points) {
  const group = L.layerGroup();
  let opacity = 1;
  const arrows = points.filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude)
    && Number.isFinite(point.u) && Number.isFinite(point.v) && Math.hypot(point.u, point.v) > 0.05);
  const draw = () => {
    group.clearLayers();
    const bounds = map.getBounds().pad(0.1);
    const zoom = map.getZoom();
    thinWindArrows(arrows, (point) => map.project([point.latitude, point.longitude], zoom), bounds).forEach((point) => {
      const speed = Math.hypot(point.u, point.v);
      const direction = (Math.atan2(point.u, point.v) * 180 / Math.PI + 360) % 360;
      const size = Math.round(Math.max(14, Math.min(28, 15 + speed * 0.55)));
      const icon = L.divIcon({
        className: "great-lakes-wind-arrow",
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        html: html`<svg viewBox="0 0 24 24" width="${size}" height="${size}" style="transform:rotate(${Math.round(direction)}deg)" aria-hidden="true"><path d="M12 2.5 18.5 11h-4.2v10.5H9.7V11H5.5Z"/></svg>`
      });
      L.marker([point.latitude, point.longitude], { icon, interactive: false, keyboard: false, opacity }).addTo(group);
    });
  };
  const originalOnAdd = group.onAdd.bind(group);
  const originalOnRemove = group.onRemove.bind(group);
  group.onAdd = (target) => {
    originalOnAdd(target);
    draw();
    map.on("zoomend moveend", draw);
  };
  group.onRemove = (target) => {
    map.off("zoomend moveend", draw);
    originalOnRemove(target);
  };
  group.setOpacity = (value) => {
    opacity = Math.max(0, Math.min(1, Number(value) || 0));
    group.eachLayer((marker) => marker.setOpacity(opacity));
    return group;
  };
  return group;
}

export function createWaveArrowLayer(map, arrows) {
  const group = L.layerGroup();
  let opacity = 1;
  const draw = () => {
    group.clearLayers();
    const bounds = map.getBounds().pad(0.1);
    const zoom = map.getZoom();
    thinWaveArrows(arrows, (arrow) => map.project([arrow.latitude, arrow.longitude], zoom), bounds).forEach((arrow) => {
      const size = Math.round(Math.max(16, Math.min(34, 16 + arrow.heightMeters * 7)));
      const icon = L.divIcon({
        className: "great-lakes-wave-arrow",
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        html: html`<svg viewBox="0 0 24 24" width="${size}" height="${size}" style="transform:rotate(${Math.round(waveTravelDegrees(arrow.directionDegrees))}deg)" aria-hidden="true"><path d="M12 2.5 18.5 11h-4.2v10.5H9.7V11H5.5Z"/></svg>`
      });
      L.marker([arrow.latitude, arrow.longitude], { icon, interactive: false, keyboard: false, opacity }).addTo(group);
    });
  };
  const originalOnAdd = group.onAdd.bind(group);
  const originalOnRemove = group.onRemove.bind(group);
  group.onAdd = (target) => {
    originalOnAdd(target);
    draw();
    map.on("zoomend moveend", draw);
  };
  group.onRemove = (target) => {
    map.off("zoomend moveend", draw);
    originalOnRemove(target);
  };
  group.setOpacity = (value) => {
    opacity = Math.max(0, Math.min(1, Number(value) || 0));
    group.eachLayer((marker) => marker.setOpacity(opacity));
    return group;
  };
  return group;
}

export function renderGreatLakesTemperatureRasters(rasters, loadRevision, expectedLayer = "temperature") {
  if (loadRevision !== greatLakesLoadRevision || greatLakesControlValue("layer") !== expectedLayer) return;
  drawPaletteRasters(rasters, expectedLayer === "air-temperature" ? "air-temperature" : "temperature", { opacity: 0.9, className: "great-lakes-temperature-raster" });
}

export function renderGreatLakesUpwellingRasters(rasters, loadRevision) {
  if (loadRevision !== greatLakesLoadRevision || greatLakesControlValue("layer") !== "upwelling") return;
  drawPaletteRasters(rasters, "upwelling", { opacity: 0.88, className: "great-lakes-upwelling-raster" });
}

// "3.2 °F colder", "1.1 °F warmer": a temperature difference in the chosen unit.
export function temperatureDifferenceLabel(differenceC, colder = "colder", warmer = "warmer") {
  const difference = Number(differenceC);
  if (!Number.isFinite(difference)) return "";
  const unit = unitPreference("waterTemperature") === "C" ? "C" : "F";
  const shown = Math.abs(unit === "F" ? difference * 1.8 : difference);
  if (shown < 0.05) return "No change";
  return `${shown.toFixed(1)} °${unit} ${difference < 0 ? colder : warmer}`;
}

export function upwellingReading(value) {
  const kind = value.kind;
  const rows = [
    Number.isFinite(value.surfaceC) ? ["Surface temperature", waterTemperatureLabel(value.surfaceC)] : null,
    Number.isFinite(value.surfaceC) && Number.isFinite(value.surroundingC) ? ["Than water nearby", temperatureDifferenceLabel(value.surfaceC - value.surroundingC)] : null,
    Number.isFinite(value.change24hC) ? ["24h model change", temperatureDifferenceLabel(value.change24hC, "cooler", "warmer")] : null,
    Number.isFinite(value.thermoclineShiftMeters) && Math.abs(value.thermoclineShiftMeters) >= 1.5
      ? ["Thermocline", `${greatLakesDepthLabel(Math.abs(value.thermoclineShiftMeters))} ${value.thermoclineShiftMeters > 0 ? "deeper" : "shallower"} than nearby`] : null,
    Number.isFinite(value.relativeChange24hC) ? ["Change relative to nearby", temperatureDifferenceLabel(value.relativeChange24hC, "more cooling", "more warming")] : null
  ];
  return {
    label: kind === "upwelling" ? "Upwelling" : kind === "downwelling" ? "Downwelling" : "Upwelling & downwelling",
    value: kind ? (value.confidence === "supported" ? "Likely" : "Possible") : value.assessment && value.assessment !== "assessed" ? "Unable to assess" : "No clear signal",
    note: kind ? (value.confidence === "supported" ? "Surface and subsurface temperature evidence agree" : "Temperature pattern suggests this; water movement is not confirmed")
      : value.openWater === false ? `Too shallow or missing data (under ${greatLakesDepthLabel(10)})`
        : value.assessment === "insufficient-reference-data" ? "Not enough nearby model water to compare"
        : value.assessment === "insufficient-stratification" ? "Not enough evidence of nearby warm water over colder water" : "",
    rows
  };
}

export function renderGreatLakesThermoclineRasters(rasters, loadRevision) {
  if (loadRevision !== greatLakesLoadRevision || greatLakesControlValue("layer") !== "thermocline") return;
  drawPaletteRasters(rasters, "thermocline", { opacity: 0.88, className: "great-lakes-thermocline-raster" });
}

export function renderGreatLakesCurrentRasters(rasters, loadRevision) {
  if (loadRevision !== greatLakesLoadRevision || greatLakesControlValue("layer") !== "currents") return;
  drawPaletteRasters(rasters, "currents", { opacity: 0.8, className: "great-lakes-current-raster" });
}

export function clearGreatLakesVisuals() {
  stopPaletteFit();
  clearGreatLakesAnimationVisuals();
  greatLakesParticleLayer?.remove();
  greatLakesParticleLayer = null;
  greatLakesWaveArrowLayer?.remove();
  greatLakesWaveArrowLayer = null;
  greatLakesConditionsLayer.clearLayers();
  document.querySelectorAll(".great-lakes-temperature-raster, .great-lakes-thermocline-raster, .great-lakes-current-raster, .great-lakes-wave-raster").forEach((image) => image.remove());
}

export function renderGreatLakesCurrents(points, zoom, target = greatLakesConditionsLayer, opacity = 1) {
  const stride = zoom <= 5 ? 12 : zoom <= 7 ? 8 : zoom <= 9 ? 5 : 3;
  const bySpeed = greatLakesFlowColor === FLOW_COLOR_BY_SPEED;
  points.filter((_, index) => index % stride === 0).forEach((point) => {
    const size = Math.round(Math.max(14, Math.min(30, 14 + point.speed * 50)));
    const fill = bySpeed ? `;fill:${paletteColor(point.speed / greatLakesCurrentSpeedMax, CURRENT_FLOW_SPEED_COLOR_STOPS)}` : "";
    const icon = L.divIcon({ className: "great-lakes-current-arrow", iconSize: [size, size], iconAnchor: [size / 2, size / 2], html: html`<svg viewBox="0 0 24 24" width="${size}" height="${size}" style="transform:rotate(${Math.round(point.direction)}deg)${fill}" aria-hidden="true"><path d="M12 2.5 19 20.5 12 16.2 5 20.5Z"/></svg>` });
    L.marker([point.latitude, point.longitude], { icon, interactive: false, keyboard: false, opacity }).addTo(target);
  });
}

// Mirror TEMPERATURE_COLOR_STOPS / CURRENT_SPEED_COLOR_STOPS in backend/great_lakes_render.py.
export const TEMPERATURE_COLOR_STOPS = [
  [0, [58, 40, 168]], [0.13, [36, 92, 226]], [0.27, [14, 152, 242]], [0.4, [12, 204, 222]],
  [0.53, [34, 208, 136]], [0.66, [150, 222, 48]], [0.78, [252, 218, 36]], [0.89, [252, 140, 28]], [1, [228, 40, 52]]
];
export const CURRENT_SPEED_COLOR_STOPS = [
  [0, [22, 58, 128]], [0.22, [40, 92, 178]], [0.45, [98, 70, 186]],
  [0.68, [172, 60, 170]], [0.86, [232, 86, 118]], [1, [252, 158, 72]]
];
export const CURRENT_COLOR_MAX_METERS_PER_SECOND = 0.5;
// Speed-coloured flow lines: brighter than the speed shading so slow (most)
// water is still visible over the map and over temperature colours.
export const CURRENT_FLOW_SPEED_COLOR_STOPS = [
  [0, [125, 225, 255]], [0.25, [70, 232, 160]], [0.5, [250, 232, 60]], [0.75, [255, 146, 40]], [1, [255, 56, 96]]
];
// Speed-coloured flow uses the loaded layer's legend range so lines match it.
export let greatLakesCurrentSpeedMax = CURRENT_COLOR_MAX_METERS_PER_SECOND;
export const SPEED_FLOW_COLOR_BINS = 10;

export function paletteRgb(position, stops) {
  const clamped = Math.max(0, Math.min(1, Number(position) || 0));
  const upper = stops.findIndex(([stop]) => clamped <= stop);
  const [highStop, highColor] = stops[Math.max(0, upper)];
  const [lowStop, lowColor] = stops[Math.max(0, upper - 1)];
  const fraction = highStop === lowStop ? 0 : (clamped - lowStop) / (highStop - lowStop);
  return lowColor.map((channel, index) => Math.round(channel + (highColor[index] - channel) * fraction)).join(", ");
}

export function paletteColor(position, stops) {
  return `rgb(${paletteRgb(position, stops)})`;
}

export function setCurrentSpeedMax(metadata = {}) {
  const maximum = Number(metadata.maxSpeedMetersPerSecond);
  greatLakesCurrentSpeedMax = Number.isFinite(maximum) && maximum > 0 ? maximum : CURRENT_COLOR_MAX_METERS_PER_SECOND;
}

// Palette bin for a speed: lines are drawn in a few batches, one per bin.
export function speedColorBin(speed, maximum = greatLakesCurrentSpeedMax, bins = SPEED_FLOW_COLOR_BINS) {
  return Math.max(0, Math.min(bins - 1, Math.floor((Number(speed) || 0) / maximum * bins)));
}

export function speedBinColors(bins = SPEED_FLOW_COLOR_BINS) {
  return Array.from({ length: bins }, (_, bin) => paletteRgb((bin + 0.5) / bins, CURRENT_FLOW_SPEED_COLOR_STOPS));
}

export function currentProfileHtml(profile, selectedDepthMeters) {
  const time = profile.historyTime
    ? historyTimeLabel(profile.validTime || profile.historyTime, { hour12: true })
    : friendlyTime(profile.validTime, Date.now(), { hour12: true });
  const source = profile.stale ? "Last available NOAA forecast" : profile.historyTime ? "Saved NOAA conditions" : "NOAA forecast";
  const surfaceOnly = profile.surfaceOnly ? " · Surface only for past samples" : "";
  return sharedCurrentProfileHtml(profile, selectedDepthMeters, {
    meta: `${time ? `${source} for ${time}` : source}${surfaceOnly}`
  });
}
export async function showGreatLakesCurrentProfile({ latitude, longitude }) {
  document.querySelector(".great-lakes-current-dialog")?.remove();
  const dialog = document.createElement("dialog");
  dialog.className = "great-lakes-current-dialog";
  setHtml(dialog, html`<form method="dialog"><button class="icon-button" aria-label="Close current profile"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 7 10 10m0-10L7 17"/></svg></button></form><h3>Underwater current by depth</h3><div data-gl-current-profile-content role="status">Loading NOAA current profile…</div>`);
  document.body.append(dialog);
  dialog.addEventListener("close", () => dialog.remove(), { once: true });
  dialog.showModal();
  const selectedDepth = greatLakesShownDepth();
  try {
    const profile = await window.noaaGreatLakesApi.currentProfile({ forecastHour: greatLakesForecastHour(), latitude, longitude, models: greatLakesLoadedModelsKey, ...greatLakesTimeParams() });
    if (dialog.isConnected) {
      const content = dialog.querySelector("[data-gl-current-profile-content]");
      content.removeAttribute("role");
      setHtml(content, profile.available ? currentProfileHtml(profile, selectedDepth) : html`<p class="great-lakes-current-empty">No current profile is available at this point. Try another point on the lake.</p>`);
    }
  } catch {
    if (dialog.isConnected) dialog.querySelector("[data-gl-current-profile-content]").textContent = "NOAA current data is unavailable. Close this view and try again shortly.";
  }
}

function pointForecastTableHtml(layer, forecast) {
  const isWind = layer === "wind";
  const rows = (forecast.points || [])
    .map((point) => ({ point, validTime: formatAppDateTime(point.validTime, { dateOptions: { year: undefined } }) }))
    .filter(({ validTime }) => validTime)
    .map(({ point, validTime }) => {
      const available = Boolean(point.available);
      const direction = point.directionDegrees === null || point.directionDegrees === undefined ? Number.NaN : Number(point.directionDegrees);
      const cardinal = Number.isFinite(direction)
        ? `${["N", "NE", "E", "SE", "S", "SW", "W", "NW"][Math.round(direction / 45) % 8]} (${Math.round(direction) % 360}°)`
        : "—";
      const firstValue = isWind && available ? windSpeedLabel(point.speedMetersPerSecond) : !isWind && available ? waveHeightLabel(point.heightMeters) : "—";
      const secondValue = isWind && available
        ? (point.gustMetersPerSecond === null || point.gustMetersPerSecond === undefined ? "—" : windSpeedLabel(point.gustMetersPerSecond))
        : !isWind && available && point.periodSeconds !== null && point.periodSeconds !== undefined
          ? `${Number(point.periodSeconds).toFixed(0)} s`
          : "—";
      return html`<tr>
        <th scope="row">${validTime}</th>
        <td data-label="${isWind ? "Wind" : "Wave height"}">${firstValue}</td>
        <td data-label="${isWind ? "Gusts" : "Period"}">${secondValue}</td>
        <td data-label="From">${available ? cardinal : "—"}</td>
      </tr>`;
    });
  if (!rows.length) return html`<p class="great-lakes-point-forecast-empty">No forecast values are available at this point.</p>`;
  return html`<div class="great-lakes-point-forecast-table-wrap">
      <table class="great-lakes-point-forecast-table">
        <thead><tr><th scope="col">Forecast time</th><th scope="col">${isWind ? "Wind" : "Wave height"}</th><th scope="col">${isWind ? "Gusts" : "Period"}</th><th scope="col">From</th></tr></thead>
        <tbody>${joinHtml(rows, "")}</tbody>
      </table>
    </div>`;
}

export async function showGreatLakesPointForecast({ layer, latitude, longitude }) {
  document.querySelector(".great-lakes-point-forecast-dialog")?.remove();
  const dialog = document.createElement("dialog");
  dialog.className = "great-lakes-current-dialog great-lakes-point-forecast-dialog";
  const label = layer === "wind" ? "Wind" : "Wave";
  setHtml(dialog, html`<form method="dialog"><button class="icon-button" aria-label="Close ${label.toLowerCase()} forecast"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 7 10 10m0-10L7 17"/></svg></button></form>
    <h3>${label} forecast</h3>
    <div data-gl-point-forecast-content role="status" aria-live="polite">Loading NOAA ${label.toLowerCase()} forecast…</div>`);
  document.body.append(dialog);
  dialog.addEventListener("close", () => dialog.remove(), { once: true });
  dialog.showModal();
  try {
    const forecast = await window.noaaGreatLakesApi.pointForecast({ layer, forecastHour: greatLakesForecastHour(), latitude, longitude, ...greatLakesTimeParams() });
    if (!dialog.isConnected) return;
    const content = dialog.querySelector("[data-gl-point-forecast-content]");
    content.removeAttribute("role");
    content.removeAttribute("aria-live");
    if (!forecast.available) {
      setHtml(content, html`<p class="great-lakes-point-forecast-empty">NOAA ${label.toLowerCase()} forecast values aren't available at this point right now. Close this view and try again shortly.</p>`);
      return;
    }
    setHtml(content, pointForecastTableHtml(layer, forecast));
  } catch {
    if (!dialog.isConnected) return;
    const content = dialog.querySelector("[data-gl-point-forecast-content]");
    content.removeAttribute("role");
    content.removeAttribute("aria-live");
    setHtml(content, html`<p class="great-lakes-point-forecast-empty">NOAA ${label.toLowerCase()} forecast couldn't be loaded. Close this view and try again shortly.</p>`);
  }
}

export async function greatLakesMapInspection(coordinates) {
  const reading = await inspectGreatLakesLayer(coordinates);
  if (!windEnabled() || greatLakesActiveLayer === "wind" || greatLakesHistoryMode() || greatLakesAnimationActive()) return reading;
  const wind = await window.noaaGreatLakesApi.windValue({ ...coordinates, forecastHour: greatLakesForecastHour(), ...greatLakesTimeParams() }).catch(() => null);
  return html`${reading}${windReading(wind)}`;
}

async function inspectGreatLakesLayer({ latitude, longitude }) {
  if (!greatLakesActiveLayer) return "";
  const depth = greatLakesShownDepth(), forecastHour = greatLakesForecastHour();
  const models = greatLakesLoadedModelsKey, timeParams = greatLakesTimeParams();
  const temperatureLabel = (value) => Number(value.depthMeters) > 0.25 ? `Temperature at ${greatLakesDepthLabel(Number(value.depthMeters))}` : "Surface temperature";
  if (greatLakesActiveLayer === "air-temperature") {
    const pinned = !greatLakesAnimationActive() || Boolean(timeParams.frame);
    const value = pinned ? await window.noaaGreatLakesApi.airTemperatureValue({ forecastHour, latitude, longitude, ...timeParams }).catch(() => null) : null;
    return readingHtml({
      label: "Air temperature",
      value: value?.available ? airTemperatureLabel(value.temperatureC) : value?.savedMapOnly ? "Map only" : "Unavailable",
      note: value?.available ? "NOAA HRRR · 2 m above the surface (not water temperature)." : value?.savedMapOnly ? "This saved sample includes the map; point readings aren't archived." : "NOAA HRRR air-temperature data is unavailable for this point and displayed forecast.",
      rows: value?.available && value.validTime ? [["Forecast valid", formatAppDateTime(value.validTime)]] : []
    });
  }
  if (greatLakesActiveLayer === "bottom-temperature") {
    try {
      const value = await window.noaaGreatLakesApi.bottomTemperatureValue({ forecastHour, latitude, longitude, ...timeParams });
      return readingHtml({
        label: "Bottom temperature",
        value: value.available ? waterTemperatureLabel(value.temperatureC) : value.savedMapOnly ? "Map only" : "Unavailable",
        note: value.savedMapOnly ? "This saved sample includes the map; point readings aren't archived." : value.available ? "" : "NOAA bottom-temperature data is unavailable here.",
        action: profileActionHtml(latitude, longitude)
      });
    } catch {
      return readingHtml({ label: "Bottom temperature", value: "Unavailable", note: "NOAA bottom-temperature data is temporarily unavailable." });
    }
  }
  if (greatLakesActiveLayer === "wind") {
    const action = greatLakesHistoryMode() ? "" : pointForecastActionHtml("wind", latitude, longitude);
    return windReading(await window.noaaGreatLakesApi.windValue({ forecastHour, latitude, longitude, ...timeParams }).catch(() => null), action, greatLakesHistoryMode());
  }
  if (greatLakesActiveLayer === "target-temperature") {
    const value = await window.noaaGreatLakesApi.targetTemperatureValue({ forecastHour, latitude, longitude, targetC: targetTemperatureC }).catch(() => null);
    if (!value?.available) return readingHtml({ label: "Target-temperature depth", value: "Unavailable" });
    return readingHtml({ label: `${waterTemperatureLabel(targetTemperatureC)} depth`,
      value: value.crossings.length ? value.crossings.map((match) => Math.abs(match.topMeters - match.bottomMeters) < 0.01 ? greatLakesDepthLabel(match.topMeters) : `${greatLakesDepthLabel(match.topMeters)}–${greatLakesDepthLabel(match.bottomMeters)}`).join(", ") : "No matching temperature",
      note: value.stale ? "Last available water-column forecast" : "Interpolated between model levels",
      action: profileActionHtml(latitude, longitude) });
  }
  if (greatLakesActiveLayer === "upwelling") {
    const value = await window.noaaGreatLakesApi.upwellingValue({ forecastHour, latitude, longitude, ...timeParams }).catch(() => null);
    if (!value?.available) return value?.savedMapOnly ? readingHtml({ label: "Upwelling & downwelling", value: "Map only", note: "Past samples keep the map, not the readings behind it" }) : "";
    return readingHtml({ ...upwellingReading(value), action: profileActionHtml(latitude, longitude) });
  }
  if (greatLakesActiveLayer === "waves") {
    const value = await window.noaaGreatLakesApi.waveValue({ forecastHour, latitude, longitude, ...timeParams }).catch(() => null);
    const action = greatLakesHistoryMode() ? "" : pointForecastActionHtml("waves", latitude, longitude);
    if (!value?.available) return readingHtml({ label: "Wave height", value: "Unavailable", note: "NOAA wave data is not available at this point right now.", action });
    return readingHtml({
      label: "Wave height",
      value: waveHeightLabel(value.heightMeters),
      rows: [
        ["Period", Number.isFinite(Number(value.periodSeconds)) ? `${Number(value.periodSeconds).toFixed(0)} s` : ""],
        ["Coming from", Number.isFinite(Number(value.directionDegrees)) ? waveDirectionText(value.directionDegrees).replace(/^From /, "") : ""]
      ],
      action
    });
  }
  if (greatLakesActiveLayer === "currents") {
    const wantsTemperature = greatLakesControlValue("current-background") === "temperature";
    const [current, temperatureValue] = await Promise.all([
      window.noaaGreatLakesApi.currentProfile({ forecastHour, latitude, longitude, models, ...timeParams }).catch(() => null),
      wantsTemperature ? window.noaaGreatLakesApi.temperatureValue({ forecastHour, depth, resolution: CURRENT_BACKGROUND_RESOLUTION, latitude, longitude, models, ...timeParams }).catch(() => null) : null
    ]);
    const values = current?.available ? (current.values || []).filter((value) => Number.isFinite(value.speedMetersPerSecond)) : [];
    if (!values.length) {
      return readingHtml({
        label: "Current",
        value: "Unavailable",
        note: "NOAA has no current profile available at this point right now.",
        action: currentProfileActionHtml(latitude, longitude)
      });
    }
    const selected = values.reduce((best, value) => Math.abs(value.depthMeters - Number(depth)) < Math.abs(best.depthMeters - Number(depth)) ? value : best, values[0]);
    const speed = selected.speedMetersPerSecond;
    const still = speed < 0.001;
    return readingHtml({
      label: Number(selected.depthMeters) > 0.25 ? `Current at ${currentProfileDepthLabel(selected.depthMeters)}` : "Surface current",
      value: currentSpeedLabel(speed),
      icon: still ? "" : directionIconHtml(selected.directionDegrees),
      note: [
        still ? "Still" : `Toward ${currentDirectionLabel(selected.directionDegrees)} · ${Math.round(selected.directionDegrees) % 360}°`,
        current.stale ? `Last available NOAA forecast: ${friendlyTime(current.validTime)}` : ""
      ].filter(Boolean).join(" · "),
      rows: [temperatureValue?.available ? [temperatureLabel(temperatureValue), waterTemperatureLabel(temperatureValue.temperatureC)] : null],
      action: currentProfileActionHtml(latitude, longitude)
    });
  }
  const resolution = GREAT_LAKES_LAYER_RESOLUTION;
  try {
    if (greatLakesActiveLayer === "thermocline") {
      const profile = await window.noaaGreatLakesApi.profile({ forecastHour, latitude, longitude, models, ...timeParams }).catch(() => null);
      if (!profile?.available) {
        return readingHtml({
          label: "Thermocline",
          value: "Unavailable",
          note: "NOAA water-column data is temporarily unavailable.",
          action: profileActionHtml(latitude, longitude)
        });
      }
      const surface = (profile.values || [])[0];
      const band = thermoclineBand(profile.thermocline);
      const thermoclineNote = band ? `Down to ${greatLakesDepthLabel(band.bottom)} · ${thicknessLabel(band)}` : profile.noThermocline === "gradual" ? "" : "Mixed top to bottom";
      return readingHtml({
        label: "Thermocline",
        value: band ? greatLakesDepthLabel(band.top) : "None",
        note: profile.stale ? ["Last available data", thermoclineNote].filter(Boolean).join(" · ") : thermoclineNote,
        rows: [surface ? ["Surface temperature", waterTemperatureLabel(surface.temperatureC)] : null],
        action: profileActionHtml(latitude, longitude)
      });
    }
    const [value, profile] = await Promise.all([
      window.noaaGreatLakesApi.temperatureValue({ forecastHour, depth, resolution, latitude, longitude, models, ...timeParams }),
      window.noaaGreatLakesApi.profile({ forecastHour, latitude, longitude, models, ...timeParams }).catch(() => null)
    ]);
    if (!value.available) {
      if (!profile?.available) return "";
      const profileValues = profile.values || [];
      const nearest = profileValues.length
        ? profileValues.reduce((best, item) => Math.abs(item.depthMeters - Number(depth)) < Math.abs(best.depthMeters - Number(depth)) ? item : best)
        : null;
      const maxProfileDepth = profileValues.length ? Number(profileValues[profileValues.length - 1].depthMeters) : -1;
      const selected = nearest && Number(depth) <= maxProfileDepth + 0.5 ? nearest : null;
      return readingHtml({
        label: selected ? temperatureLabel(selected) : "Water temperature",
        value: selected ? waterTemperatureLabel(selected.temperatureC) : "Unavailable",
        note: profile.stale ? "Last available data" : "",
        rows: [profile.available ? ["Thermocline", thermoclineRangeLabel(profile.thermocline, profile.noThermocline)] : null],
        action: profileActionHtml(latitude, longitude)
      });
    }
    return readingHtml({
      label: temperatureLabel(value),
      value: waterTemperatureLabel(value.temperatureC),
      rows: [
        profile?.available ? ["Thermocline", thermoclineRangeLabel(profile.thermocline, profile.noThermocline)] : null,
        profile?.stale ? ["Water-column data", "Last available"] : null
      ],
      action: profileActionHtml(latitude, longitude)
    });
  } catch { return ""; }
}

// "55–66 ft" for a card row; "None (mixed)" or "None" without a thermocline.
export function thermoclineRangeLabel(thermocline, noThermocline) {
  return depthRangeLabel(thermoclineBand(thermocline)) || (noThermocline === "gradual" ? "None" : "None (mixed)");
}

// Particles per 1280×800 CSS pixels of visible water, independent of screen DPI.
export const CURRENT_PARTICLE_DENSITY = { low: 2500, medium: 4500, high: 9500, "very-high": 14000 };
const CURRENT_FRAME_MS = 1000 / 60;
const CURRENT_TRAIL_RETENTION = 0.972;

export function currentParticleCount(waterArea, density = greatLakesControlValue("density")) {
  const baseDensity = CURRENT_PARTICLE_DENSITY[density] || CURRENT_PARTICLE_DENSITY.medium;
  return Math.round(baseDensity * Math.min(1.8, Math.max(0, waterArea) / (1280 * 800)));
}

// Index native shoreline edges by latitude so particle checks retain every
// model vertex without scanning the entire shoreline on every animation tick.
const decodedModelShorelines = new WeakMap();
export function decodeModelShoreline(rings) {
  if (!rings?.length) return null;
  if (decodedModelShorelines.has(rings)) return decodedModelShorelines.get(rings);
  const edges = rings.flatMap((ring) => ring.map((point, index) => [point, ring[(index + 1) % ring.length]]));
  let south = Infinity, north = -Infinity;
  for (const [[, a], [, b]] of edges) { south = Math.min(south, a, b); north = Math.max(north, a, b); }
  if (!(north > south)) { decodedModelShorelines.set(rings, null); return null; }
  const buckets = Array.from({ length: 256 }, () => []);
  const bucket = (lat) => Math.max(0, Math.min(255, Math.floor((lat - south) / (north - south) * 256)));
  for (const edge of edges) {
    const low = bucket(Math.min(edge[0][1], edge[1][1])), high = bucket(Math.max(edge[0][1], edge[1][1]));
    for (let index = low; index <= high; index += 1) buckets[index].push(edge);
  }
  const shoreline = (latitude, longitude) => {
    if (latitude < south || latitude > north) return false;
    let inside = false;
    for (const [[ax, ay], [bx, by]] of buckets[bucket(latitude)]) {
      if ((ay > latitude) !== (by > latitude) && longitude < ax + (latitude - ay) * (bx - ax) / (by - ay)) inside = !inside;
    }
    return inside;
  };
  decodedModelShorelines.set(rings, shoreline);
  return shoreline;
}

let modelShorelineRequest = null;
function loadModelShorelines() {
  if (!modelShorelineRequest) modelShorelineRequest = fetch("/api/v1/shoreline").then((response) => {
    if (!response.ok) throw new Error("Native shoreline unavailable");
    return response.json();
  }).then((payload) => payload.outlines).catch((error) => { modelShorelineRequest = null; throw error; });
  return modelShorelineRequest;
}

export function decodeGreatLakesWaterMask(mask) {
  if (!mask?.bits || !(mask.rows > 1) || !(mask.columns > 1)) return null;
  let bytes = mask.bits;
  if (!(bytes instanceof Uint8Array)) {
    const binary = atob(bytes);
    bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  }
  const rowStep = (mask.latitudeEnd - mask.latitudeStart) / (mask.rows - 1);
  const columnStep = (mask.longitudeEnd - mask.longitudeStart) / (mask.columns - 1);
  const shoreline = decodeModelShoreline(mask.rings);
  return (latitude, longitude) => {
    if (shoreline && !shoreline(latitude, longitude)) return false;
    if (shoreline && !mask.depthLimited) return true;
    const row = Math.round((latitude - mask.latitudeStart) / rowStep);
    const column = Math.round((longitude - mask.longitudeStart) / columnStep);
    if (row < 0 || row >= mask.rows || column < 0 || column >= mask.columns) return false;
    const index = row * mask.columns + column;
    return Boolean((bytes[index >> 3] >> (index & 7)) & 1);
  };
}

export function createCurrentFieldSampler(fields, isAllowedPoint = null) {
  if (fields[0]?.points) {
    const points = fields.flatMap((field) => field.points);
    const smooth = fields[0].smooth === true;
    const buckets = new Map();
    for (const point of points) {
      const key = `${Math.floor(point.latitude / 0.1)}:${Math.floor(point.longitude / 0.15)}`;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(point);
    }
    return (latitude, longitude) => {
      if (isAllowedPoint && !isAllowedPoint(latitude, longitude)) return null;
      let best = null, bestDistance = Infinity;
      let total = 0, u = 0, v = 0;
      const longitudeKm = 111.2 * Math.cos(latitude * Math.PI / 180);
      const row = Math.floor(latitude / 0.1), column = Math.floor(longitude / 0.15);
      const rowRadius = smooth ? 2 : 1;
      const columnRadius = smooth ? Math.ceil(18 / (0.15 * longitudeKm)) : 2;
      for (let y = row - rowRadius; y <= row + rowRadius; y += 1) for (let x = column - columnRadius; x <= column + columnRadius; x += 1) {
        for (const point of buckets.get(`${y}:${x}`) || []) {
          const dy = (point.latitude - latitude) * 111.2;
          const dx = (point.longitude - longitude) * longitudeKm;
          const distance = dx * dx + dy * dy;
          if (distance < bestDistance) { best = point; bestDistance = distance; }
          if (smooth && distance < 18 * 18) {
            // A compact, smooth kernel blends travel vectors across sample
            // boundaries and fades each neighbor's influence to zero at 18 km.
            // Blend components, not bearings, so 359° and 1° stay near north.
            const radius = Math.sqrt(distance) / 18;
            const weight = (1 - radius) ** 4 * (1 + 4 * radius);
            total += weight;
            u += point.u * weight;
            v += point.v * weight;
          }
        }
      }
      // Wave direction samples are about 10 km apart. Stop the field at its
      // edge instead of letting particles cross large unsampled gaps.
      if (smooth) return total > 0 ? { u: u / total, v: v / total } : null;
      return bestDistance <= 18 * 18 ? { u: best.u, v: best.v } : null;
    };
  }
  const prepared = fields.map((field) => {
    const ys = field.latitudeAxis, xs = field.longitudeAxis;
    return {
      field, ys, xs, isWater: decodeGreatLakesWaterMask(field.waterMask),
      yMin: Math.min(ys[0], ys[ys.length - 1]), yMax: Math.max(ys[0], ys[ys.length - 1]),
      xMin: Math.min(xs[0], xs[xs.length - 1]), xMax: Math.max(xs[0], xs[xs.length - 1])
    };
  });
  const find = (axis, value) => {
    let low = 0, high = axis.length - 1;
    const ascending = axis[high] > axis[0];
    while (high - low > 1) { const middle = (low + high) >> 1; if (ascending ? axis[middle] < value : axis[middle] > value) low = middle; else high = middle; }
    return low;
  };
  return (latitude, longitude) => {
    if (isAllowedPoint && !isAllowedPoint(latitude, longitude)) return null;
    for (const { field, ys, xs, isWater, yMin, yMax, xMin, xMax } of prepared) {
      if (latitude < yMin || latitude > yMax || longitude < xMin || longitude > xMax) continue;
      if (isWater && !isWater(latitude, longitude)) continue;
      const row = find(ys, latitude), column = find(xs, longitude), index = row * field.columns + column;
      const ids = [index, index + 1, index + field.columns, index + field.columns + 1];
      const fy = (latitude - ys[row]) / (ys[row + 1] - ys[row]), fx = (longitude - xs[column]) / (xs[column + 1] - xs[column]);
      const weights = [(1 - fx) * (1 - fy), fx * (1 - fy), (1 - fx) * fy, fx * fy];
      // With a fine water mask the server has already extended velocities to
      // the shoreline; without one, only the coarse wet corners are usable.
      const corners = isWater ? [0, 1, 2, 3] : [0, 1, 2, 3].filter((position) => field.mask[ids[position]]);
      if (!corners.length) continue;
      const total = corners.reduce((sum, position) => sum + weights[position], 0);
      const blend = (values) => total ? corners.reduce((sum, position) => sum + values[ids[position]] * weights[position], 0) / total : values[ids[corners[0]]];
      return { u: blend(field.u), v: blend(field.v) };
    }
    return null;
  };
}

// NOAA directions describe where waves come from. Convert to the travel
// bearing. Particle speed follows wave height (particle-speed.js): still at
// flat water, creeping through calm and chop, racing at 8 ft and above.
export function waveParticleSpeed(heightMeters) {
  return dramaticParticleSpeed(heightMeters, WAVE_DEFAULT_MAX_METERS);
}

export function waveParticleFields(arrows) {
  const points = arrows.flatMap((arrow) => {
    if (!Number.isFinite(arrow.directionDegrees)) return [];
    const bearing = (arrow.directionDegrees + 180) * Math.PI / 180;
    const speed = waveParticleSpeed(arrow.heightMeters);
    return [{ latitude: arrow.latitude, longitude: arrow.longitude, u: Math.sin(bearing) * speed, v: Math.cos(bearing) * speed }];
  });
  return points.length ? [{ points, smooth: true }] : [];
}

export { currentPixelsPerFrame };

export function blendCurrentVectors(from, to, fraction) {
  if (!from || !to) return fraction < 0.5 ? from : to;
  const mix = Math.max(0, Math.min(1, fraction));
  return { u: from.u + (to.u - from.u) * mix, v: from.v + (to.v - from.v) * mix };
}

export function createParticleLayer(map, fields, { clipToGreatLakesWater = false, particleColor = null, density = null } = {}) {
  const canvas = L.DomUtil.create("canvas", "great-lakes-current-flow leaflet-layer");
  // Keep the visible canvas GPU-backed: willReadFrequently forces software
  // drawing of every full-screen trail fade, which stalls low-power devices.
  // The once-a-second residue cleanup tolerates a GPU readback.
  const ctx = canvas.getContext("2d");
  const waterMaskCanvas = clipToGreatLakesWater ? document.createElement("canvas") : null;
  const waterMaskCtx = waterMaskCanvas?.getContext("2d", { willReadFrequently: true });
  let waterMaskPixels = null, waterMaskWidth = 0, waterMaskHeight = 0, waterMaskFailed = false;
  // Replaced when an animation moves to the next frame; particles keep flowing.
  let sample;
  const speedColors = speedBinColors();
  let explicitParticleColor = particleColor;
  const particles = [];
  let seeds = [];
  const particleDensity = density || greatLakesControlValue("density");
  const speedScale = { slow: 0.5, normal: 1, fast: 1.9 }[greatLakesControlValue("animation-speed")] || 1;
  let frame = null, last = 0, active = true, origin = L.point(0, 0), size = L.point(0, 0), zoom = null;
  let nativeOutlines = null;
  if (clipToGreatLakesWater) {
    loadModelShorelines().then((outlines) => { nativeOutlines = outlines; if (active) refresh(); })
      .catch(() => { waterMaskFailed = true; waterMaskPixels = null; });
  }
  // Warm-up frames are excluded: image decoding makes the first second slow.
  let frameTime = 16.67, framesSinceTrim = -120, minimumCount = 0;
  let fieldTransition = null;
  let trailDecaySteps = 0, trailCleanupSteps = 0;

  const toLatLng = (x, y) => map.layerPointToLatLng([x, y]);
  const inView = (x, y) => x >= origin.x && y >= origin.y && x <= origin.x + size.x && y <= origin.y + size.y;

  function rebuildWaterMask() {
    if (!waterMaskCanvas || !waterMaskCtx) return;
    waterMaskCanvas.width = Math.max(1, Math.ceil(size.x));
    waterMaskCanvas.height = Math.max(1, Math.ceil(size.y));
    waterMaskWidth = waterMaskCanvas.width;
    waterMaskHeight = waterMaskCanvas.height;
    waterMaskPixels = null;
    if (waterMaskFailed || !nativeOutlines) return;
    waterMaskCtx.clearRect(0, 0, waterMaskWidth, waterMaskHeight);
    waterMaskCtx.imageSmoothingEnabled = true;
    try {
      for (const outline of nativeOutlines || []) {
        waterMaskCtx.beginPath();
        for (const ring of outline.rings) {
          ring.forEach(([longitude, latitude], index) => {
            const point = map.latLngToContainerPoint([latitude, longitude]);
            if (index === 0) waterMaskCtx.moveTo(point.x, point.y);
            else waterMaskCtx.lineTo(point.x, point.y);
          });
          waterMaskCtx.closePath();
        }
        waterMaskCtx.fillStyle = "white";
        waterMaskCtx.fill("evenodd");
      }
      const rgba = waterMaskCtx.getImageData(0, 0, waterMaskWidth, waterMaskHeight).data;
      const waterPixels = waterMaskWidth * waterMaskHeight;
      const packed = new Uint8Array(Math.ceil(waterPixels / 8));
      for (let index = 0; index < waterPixels; index += 1) {
        if (rgba[index * 4 + 3] > 48) packed[index >> 3] |= 1 << (index & 7);
      }
      waterMaskPixels = packed;
    } catch {
      // A failed local mask should hide wave flow instead of drawing it over land.
      waterMaskFailed = true;
      waterMaskPixels = null;
    }
  }

  function isLakeWater(latitude, longitude) {
    if (!waterMaskPixels) return false;
    const point = map.latLngToContainerPoint([latitude, longitude]);
    const x = Math.floor(point.x), y = Math.floor(point.y);
    if (x < 0 || y < 0 || x >= waterMaskWidth || y >= waterMaskHeight) return false;
    const index = y * waterMaskWidth + x;
    return Boolean((waterMaskPixels[index >> 3] >> (index & 7)) & 1);
  }

  const allowedPoint = clipToGreatLakesWater ? isLakeWater : null;
  sample = createCurrentFieldSampler(fields, allowedPoint);

  function spawn(particle) {
    particle.vector = null;
    if (!seeds.length) return particle;
    for (let attempt = 0; attempt < 6; attempt += 1) {
      const seed = seeds[Math.floor(Math.random() * seeds.length)];
      const x = seed.x + (Math.random() - 0.5) * seed.width;
      const y = seed.y + (Math.random() - 0.5) * seed.height;
      const latlng = toLatLng(x, y);
      const vector = sample(latlng.lat, latlng.lng);
      // Fast water paints more pixels, so give slower eddies more seeds.
      if (vector && currentPixelsPerFrame(Math.hypot(vector.u, vector.v)) && Math.random() * (1 + 2.2 * Math.hypot(vector.u, vector.v)) < 1) {
        particle.x = x;
        particle.y = y;
        particle.vector = vector;
        particle.age = 0;
        particle.maxAge = 120 + Math.random() * 200;
        particle.wait = 0;
        return particle;
      }
    }
    // Mostly-land views would otherwise retry every particle every frame.
    particle.wait = 8 + Math.floor(Math.random() * 24);
    return particle;
  }

  function reset() {
    // Stratify the spawn area. Land-heavy views get fewer particles rather
    // than concentrating a whole screen's budget in a narrow strip of water.
    seeds = [];
    let waterArea = 0;
    for (let y = 0; y < size.y; y += 24) for (let x = 0; x < size.x; x += 24) {
      const width = Math.min(24, size.x - x), height = Math.min(24, size.y - y);
      const point = { x: origin.x + x + width / 2, y: origin.y + y + height / 2, width, height };
      const latlng = toLatLng(point.x, point.y);
      const vector = sample(latlng.lat, latlng.lng);
      if (!vector || !currentPixelsPerFrame(Math.hypot(vector.u, vector.v))) continue;
      seeds.push(point);
      waterArea += width * height;
    }
    const count = currentParticleCount(waterArea, particleDensity);
    minimumCount = Math.round(count * 0.45);
    // Preserve moving heads through a pan/resize; only new slots are seeded.
    if (map.getZoom() !== zoom) particles.length = 0;
    particles.length = Math.min(particles.length, count);
    for (let index = particles.length; index < count; index += 1) {
      const particle = spawn({});
      particle.age = Math.random() * (particle.maxAge || 0);
      particles.push(particle);
    }
  }

  function position() {
    size = map.getSize();
    origin = map.containerPointToLayerPoint([0, 0]);
    const ratio = Math.min(2, window.devicePixelRatio || 1);
    L.DomUtil.setPosition(canvas, origin);
    canvas.width = Math.round(size.x * ratio);
    canvas.height = Math.round(size.y * ratio);
    canvas.style.width = `${size.x}px`;
    canvas.style.height = `${size.y}px`;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    trailDecaySteps = trailCleanupSteps = 0;
    rebuildWaterMask();
  }

  function draw(steps, elapsedSteps = steps) {
    // Fade the existing image by elapsed time, then paint only each head's new
    // movement. No discrete tail commits, shifting arrays or full trail redraws.
    // Motion is capped after a slow frame so particles do not jump across the
    // map, but trail decay must use real elapsed time or old streaks build up
    // whenever rendering falls below 24 fps.
    // Canvas stores 8-bit alpha: repeated multiplication stops fading faint
    // pixels once rounding wins. Fade at most 60 times/second (including on
    // high-refresh displays), then remove that residue once per second.
    trailDecaySteps += elapsedSteps;
    trailCleanupSteps += elapsedSteps;
    if (trailDecaySteps >= 1 - 1e-8) {
      ctx.globalCompositeOperation = "destination-in";
      ctx.fillStyle = `rgba(0, 0, 0, ${CURRENT_TRAIL_RETENTION ** trailDecaySteps})`;
      ctx.fillRect(0, 0, size.x, size.y);
      trailDecaySteps = 0;
    }
    if (trailCleanupSteps >= 60) {
      const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
      for (let index = 3; index < image.data.length; index += 4) {
        if (image.data[index] <= 20) image.data[index] = 0;
      }
      ctx.putImageData(image, 0, 0);
      trailCleanupSteps = 0;
    }
    ctx.globalCompositeOperation = "source-over";
    if (clipToGreatLakesWater && !waterMaskPixels) return;

    const { rgb, halo, bySpeed } = explicitParticleColor ? { ...explicitParticleColor, bySpeed: false } : flowColorParts(greatLakesFlowColor);
    const paths = new Array(bySpeed ? SPEED_FLOW_COLOR_BINS : 3).fill(null);
    for (const particle of particles) {
      if (particle.wait > 0) { particle.wait = Math.max(0, particle.wait - steps); continue; }
      if (!particle.vector || particle.age >= particle.maxAge || !inView(particle.x, particle.y)) {
        spawn(particle);
        if (!particle.vector) continue;
      }
      const speed = Math.hypot(particle.vector.u, particle.vector.v);
      const distance = currentPixelsPerFrame(speed) * speedScale * steps;
      if (!distance) { particle.vector = null; continue; }
      const x = particle.x, y = particle.y;
      const nextX = x + particle.vector.u / speed * distance;
      const nextY = y - particle.vector.v / speed * distance;
      if (!inView(nextX, nextY)) { particle.vector = null; continue; }
      const next = toLatLng(nextX, nextY);
      const vector = sample(next.lat, next.lng);
      if (!vector) { particle.vector = null; continue; }
      particle.x = nextX;
      particle.y = nextY;
      particle.vector = vector;
      particle.age += steps;
      const bin = bySpeed ? speedColorBin(speed, greatLakesCurrentSpeedMax) : speed > 0.4 ? 2 : speed > 0.1 ? 1 : 0;
      const path = paths[bin] || (paths[bin] = new Path2D());
      path.moveTo(x - origin.x, y - origin.y);
      path.lineTo(nextX - origin.x, nextY - origin.y);
    }
    ctx.lineCap = "round";
    paths.forEach((path, bin) => {
      if (!path) return;
      // A restrained outline keeps coloured/black flow readable on any basemap.
      ctx.lineWidth = bySpeed ? 2.2 : 1.8;
      ctx.strokeStyle = `rgba(${halo}, ${1 - 0.88 ** steps})`;
      ctx.stroke(path);
      ctx.lineWidth = bySpeed ? 1.3 : 1.05;
      const opacity = bySpeed ? 0.55 : [0.3, 0.46, 0.5][bin];
      ctx.strokeStyle = `rgba(${bySpeed ? speedColors[bin] : rgb}, ${1 - (1 - opacity) ** steps})`;
      ctx.stroke(path);
    });
    if (waterMaskCanvas) {
      ctx.save();
      ctx.globalCompositeOperation = "destination-in";
      ctx.drawImage(waterMaskCanvas, 0, 0, size.x, size.y);
      ctx.restore();
    }
  }

  function tick(now) {
    if (!active) return;
    frame = requestAnimationFrame(tick);
    if (document.hidden || canvas.style.visibility === "hidden") { last = 0; return; }
    const elapsed = last ? now - last : CURRENT_FRAME_MS;
    const elapsedSteps = elapsed / CURRENT_FRAME_MS;
    const steps = Math.min(2.5, elapsedSteps);
    last = now;
    // Retire completed blends rather than retaining a chain of old fields.
    if (fieldTransition && now >= fieldTransition.ends) {
      sample = fieldTransition.target;
      fieldTransition = null;
    }
    frameTime = frameTime * 0.95 + Math.min(elapsed, 100) * 0.05;
    if (++framesSinceTrim > 60 && frameTime > 22 && particles.length > minimumCount) {
      particles.length = Math.max(minimumCount, Math.round(particles.length * 0.85));
      framesSinceTrim = 0;
    }
    draw(steps, elapsedSteps);
  }

  function hide() { canvas.style.visibility = "hidden"; }
  function refresh() {
    position();
    reset();
    zoom = map.getZoom();
    last = 0;
    canvas.style.visibility = "";
  }

  return {
    addTo() {
      map.getPane("overlayPane").appendChild(canvas);
      refresh();
      map.on("zoomstart", hide);
      map.on("resize viewreset moveend", refresh);
      frame = requestAnimationFrame(tick);
      return this;
    },
    setFields(next, { durationMs = 0 } = {}) {
      const target = createCurrentFieldSampler(next, allowedPoint);
      if (!durationMs) { sample = target; fieldTransition = null; return; }
      const previous = sample, started = performance.now();
      fieldTransition = { target, ends: started + durationMs };
      sample = (latitude, longitude) => {
        const fraction = Math.min(1, (performance.now() - started) / durationMs);
        if (fraction >= 1) return target(latitude, longitude);
        return blendCurrentVectors(previous(latitude, longitude), target(latitude, longitude), fraction);
      };
    },
    setParticleColor(next) {
      explicitParticleColor = next;
      ctx.clearRect(0, 0, size.x, size.y);
    },
    remove() {
      active = false;
      cancelAnimationFrame(frame);
      map.off("zoomstart", hide);
      map.off("resize viewreset moveend", refresh);
      canvas.remove();
    }
  };
}

// The server keeps NOAA data current; an open page checks this often whether
// a new run or the next hourly frame is being served and reloads only then.
export const GREAT_LAKES_STATUS_POLL_MS = 5 * 60 * 1000;
const greatLakesStatusCache = new Map();
export let greatLakesLoadedDataVersion = "";
let greatLakesLayerAvailability = null;

export async function greatLakesDataStatus(modelsKey, { fresh = false } = {}) {
  const cached = greatLakesStatusCache.get(modelsKey);
  if (!fresh && cached && Date.now() - cached.at < 60 * 1000) return cached.status;
  try {
    const status = await window.noaaGreatLakesApi.status({ models: modelsKey, signal: AbortSignal.timeout(5000) });
    greatLakesStatusCache.set(modelsKey, { status, at: Date.now() });
    return status;
  } catch {
    return cached?.status || null;
  }
}

// "tonight at 10 PM", "tomorrow at 4 AM", "Sunday at 10:30 PM" instead of a numeric date.
export function friendlyTime(value, now = Date.now(), options) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const dayNumber = (time) => {
    const parts = appDateParts(time);
    return parts ? Date.UTC(parts.year, parts.month - 1, parts.day) / 86400000 : Number.NaN;
  };
  const days = Math.round(dayNumber(date) - dayNumber(now));
  const parts = appDateParts(date);
  const time = formatClockTime(date, options);
  const day = days === 0 ? (parts.hour >= 18 ? "tonight" : "today")
    : days === 1 ? "tomorrow"
    : days === -1 ? "yesterday"
    : days < -1 || days > 6 ? formatAppDate(date, { weekday: "short" })
    : formatAppDate(date, { weekday: "long", month: undefined, day: undefined });
  return `${day} at ${time}`;
}

export function friendlyWait(milliseconds) {
  const minutes = Math.round(milliseconds / 60000);
  if (minutes < 2) return "in a minute or so";
  if (minutes < 55) return `in about ${minutes} minutes`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? "in about an hour" : `in about ${hours} hours`;
}

// When NOAA produced the runs behind a layer, from /status, for drawings that do not name their run.
export function statusRunTimes(status, layer) {
  const times = layer === "air-temperature" ? [status?.airTemperature?.runTime]
    : layer === "waves" ? [status?.waves?.runTime] : Object.values(status?.models || {}).map((model) => model.runTime);
  return times.map((time) => Date.parse(time || "")).filter(Number.isFinite);
}

export async function refreshGreatLakesIfStale(map) {
  if (!map || !greatLakesActiveLayer || !greatLakesLoadedModelsKey || document.hidden) return false;
  if (greatLakesHistoryMode()) {
    // A past sample never changes; newly saved samples just become choices.
    await loadGreatLakesHistoryIndex({ fresh: true });
    syncHistoryInput();
    return false;
  }
  if (greatLakesActiveLayer !== "wind" || !greatLakesAnimationActive()) refreshWind();
  if (greatLakesActiveLayer === "target-temperature" || greatLakesActiveLayer === "wind") {
    await loadGreatLakesConditions(map);
    return true;
  }
  const status = await greatLakesDataStatus(greatLakesLoadedModelsKey, { fresh: true });
  const version = greatLakesDataVersion(status, greatLakesActiveLayer);
  const retryingLayer = ["fallback", "waiting", "error"].includes(greatLakesLayerAvailability);
  if (!version || (version === greatLakesLoadedDataVersion && !retryingLayer)) return false;
  if (retryingLayer && version === greatLakesLoadedDataVersion) {
    for (const key of greatLakesPayloadCache.keys()) {
      try {
        if (JSON.parse(key).layer === greatLakesActiveLayer) greatLakesPayloadCache.delete(key);
      } catch { /* Ignore cache keys from older app versions. */ }
    }
  }
  await loadGreatLakesConditions(map);
  return true;
}

// Atmospheric and wave runs advance independently of NOAA's lake models.
export function greatLakesDataVersion(status, layer) {
  if (layer === "air-temperature") return status?.airTemperatureVersion || "";
  return (layer === "waves" ? status?.wavesVersion : status?.version) || "";
}

// Lets companion overlays (measurement points) match the visible temperature colours.
export function announceGreatLakesLayer(layer, metadata, temperatureMetadata = null) {
  document.dispatchEvent(new CustomEvent("great-lakes-layer-loaded", { detail: { layer, metadata, temperatureMetadata, depth: Number(greatLakesShownDepth()) || 0 } }));
}

// NOAA stores fixed depth levels (0, 1, 2, 4, 6 m …); the server shows the
// level nearest the slider, which can differ from the requested depth.
export function modelDepthShown(metadata = {}) {
  const depths = (metadata.models || []).map((model) => Number(model.selectedDepthMeters)).filter(Number.isFinite);
  return depths.length ? depths[0] : null;
}

export function modelDepthNote(requestedMeters, shownMeters) {
  if (!Number.isFinite(shownMeters) || Math.abs(shownMeters - requestedMeters) < 0.3) return "";
  return ` Showing the model's ${greatLakesDepthLabel(shownMeters)} level, the closest to ${greatLakesDepthLabel(requestedMeters)}.`;
}

const MODEL_LAKE_NAMES = { LSOFS: "Lake Superior", LMHOFS: "Lakes Michigan and Huron", LEOFS: "Lake Erie", LOOFS: "Lake Ontario" };

function lakeList(models) {
  const names = [...new Set(models.map((model) => MODEL_LAKE_NAMES[model] || model))];
  return names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

// When NOAA produced the model run behind a drawing ("20261015t06z" is 06 UTC on October 15).
export function noaaRunTime(source = {}) {
  const stated = Date.parse(source.runTime || "");
  if (Number.isFinite(stated)) return stated;
  if (Number.isFinite(Number(source.cycleEpoch)) && source.cycleEpoch !== null) return Number(source.cycleEpoch) * 1000;
  const match = /^(\d{4})(\d{2})(\d{2})t(\d{2})z$/i.exec(String(source.run || ""));
  return match ? Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4])) : Number.NaN;
}

export function noaaGeneratedLabel(times) {
  const valid = times.filter(Number.isFinite);
  return valid.length ? `NOAA data generated ${formatAppDateTime(Math.max(...valid), { dateOptions: { year: undefined } })}.` : "";
}

// The panel's status line: when NOAA generated the data on the map, with an
// alert only when NOAA is not returning usable data for one or more lakes.
export function noaaDataStatus(metadataList, statusTimes = [], { historical = false } = {}) {
  const metadata = metadataList.filter(Boolean);
  // Single-source layers (wind) describe their run on the drawing itself.
  const models = metadata.flatMap((item) => item.models?.length ? item.models : [item]);
  const missing = new Set(models.filter((model) => model.available === false && model.model).map((model) => model.model));
  const stale = new Set(models.filter((model) => model.stale && model.model).map((model) => model.model));
  for (const item of metadata) {
    // A whole older drawing kept while NOAA fails names the lakes it lacks.
    if (item.availability?.state === "fallback") {
      for (const model of item.availability.fallbackModels || item.availability.missingModels || []) if (!missing.has(model)) stale.add(model);
    }
  }
  const shown = models.filter((model) => model.available !== false && !model.tooShallow);
  if (!shown.length && (missing.size || metadata.some((item) => item.available === false))) {
    if (historical) return { message: noaaGeneratedLabel(metadata.map(noaaRunTime)), error: false };
    return { message: "NOAA data is unavailable right now.", error: true };
  }
  const fresh = shown.filter((model) => !stale.has(model.model));
  const generated = historical
    ? noaaGeneratedLabel(shown.map(noaaRunTime)) || noaaGeneratedLabel(metadata.map(noaaRunTime))
    : noaaGeneratedLabel(fresh.length ? fresh.map(noaaRunTime) : []) || noaaGeneratedLabel(statusTimes);
  if (historical) return { message: generated, error: false };
  const alerts = [];
  if (missing.size) alerts.push(`NOAA isn't returning data for ${lakeList([...missing])} right now.`);
  if (stale.size && !fresh.length) {
    const older = noaaGeneratedLabel(shown.map(noaaRunTime));
    return { message: `NOAA isn't returning new data right now; showing the most recent available.${older ? ` ${older}` : ""}`, error: true };
  }
  if (stale.size) alerts.push(`NOAA isn't returning new data for ${lakeList([...stale])}; showing the most recent available.`);
  return { message: [...alerts, generated].filter(Boolean).join(" "), error: alerts.length > 0 };
}

// Lakes left blank because the chosen depth is deeper than they go.
export function tooShallowNote(models = []) {
  const shallow = models.filter((model) => model.tooShallow);
  const names = [...new Set(shallow.map((model) => MODEL_LAKE_NAMES[model.model] || model.model))];
  if (!names.length) return "";
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return ` ${list} ${names.length === 1 && !list.startsWith("Lakes ") ? "isn't" : "aren't"} this deep, so ${names.length === 1 && !list.startsWith("Lakes ") ? "it's" : "they're"} left blank.`;
}

export async function loadGreatLakesConditions(map) {
  if (!greatLakesConditionsLayer) return;
  const loadRevision = ++greatLakesLoadRevision;
  const layer = greatLakesControlValue("layer");
  greatLakesConditionsRequest?.abort();
  const historyMode = greatLakesHistoryMode();
  const windAnimation = layer === "wind" && greatLakesAnimationActive();
  if (historyMode && layer === "wind") refreshWind();
  else if (!["target-temperature", "wind"].includes(layer) || windAnimation) refreshWind();
  if (layer === "target-temperature" || (layer === "wind" && !windAnimation && !historyMode)) {
    stopGreatLakesAnimation();
    document.body.classList.remove("great-lakes-layer-none");
    clearGreatLakesVisuals();
    greatLakesActiveLayer = layer;
    greatLakesLoadedModelsKey = GREAT_LAKES_ALL_MODELS.join(",");
    greatLakesConditionsRequest = new AbortController();
    setGreatLakesMapLoading(true);
    if (layer === "target-temperature") {
      setTargetTemperatureLegendRange(null);
      setTargetTemperatureSurfaceRange(null, "loading");
    }
    setGreatLakesStatus(layer === "wind" ? "Loading NOAA wind forecast…" : "Loading NOAA forecast…");
    try {
      const payload = layer === "wind" ? await refreshWind() : await window.noaaGreatLakesApi.conditions({ layer, forecastHour: greatLakesControlValue("forecast"), targetC: targetTemperatureC, selection: greatLakesControlValue("target-selection"), signal: greatLakesConditionsRequest.signal });
      if (loadRevision !== greatLakesLoadRevision) return;
      if (!payload) throw new Error("Forecast unavailable");
      if (layer === "target-temperature") {
        setTargetTemperatureLegendRange(payload.metadata);
        setTargetTemperatureSurfaceRange(payload.metadata);
        for (const raster of payload.rasters || []) L.imageOverlay(raster.imageUrl, raster.bounds, { opacity: 0.8, interactive: false }).addTo(greatLakesConditionsLayer);
        refreshWind();
      }
      if (layer === "wind") drawPaletteRasters(payload.rasters || [], "wind", { opacity: 0.8, className: "great-lakes-wind-raster" });
      const { message, error } = noaaDataStatus([payload.metadata]);
      setGreatLakesStatus(message, error);
      announceGreatLakesLayer(layer, payload.metadata);
    } catch (error) {
      if (loadRevision === greatLakesLoadRevision && layer === "target-temperature" && error.name !== "AbortError") setTargetTemperatureSurfaceRange(null, "error");
      if (loadRevision === greatLakesLoadRevision && error.name !== "AbortError") setGreatLakesStatus("NOAA data is unavailable right now.", true);
    } finally {
      if (loadRevision === greatLakesLoadRevision) setGreatLakesMapLoading(false);
    }
    return;
  }
  if (!layer) {
    stopGreatLakesAnimation();
    setGreatLakesMapLoading(false);
    document.body.classList.add("great-lakes-layer-none");
    clearGreatLakesVisuals();
    greatLakesActiveLayer = "";
    greatLakesLoadedModelsKey = "";
    greatLakesLayerAvailability = null;
    setGreatLakesStatus("Choose a layer to show NOAA Great Lakes conditions.");
    announceGreatLakesLayer("", {});
    return;
  }
  document.body.classList.remove("great-lakes-layer-none");
  const resolution = GREAT_LAKES_LAYER_RESOLUTION;
  const models = GREAT_LAKES_ALL_MODELS;
  const modelsKey = models.join(",");
  setGreatLakesMapLoading(true);
  if (historyMode) {
    stopGreatLakesAnimation();
    setGreatLakesStatus("Loading saved conditions…");
    const ready = await ensureGreatLakesHistoryTime(layer);
    if (loadRevision !== greatLakesLoadRevision) return;
    announceHistoryTime();
    if (!ready) {
      clearGreatLakesVisuals();
      greatLakesActiveLayer = "";
      if (layer === "wind") showWindPayload(null, { historical: true });
      setGreatLakesMapLoading(false);
      setGreatLakesStatus(greatLakesHistoryIndex
        ? "No saved conditions are available yet."
        : "Saved conditions are unavailable right now.", !greatLakesHistoryIndex);
      announceGreatLakesLayer("", {});
      return;
    }
  }
  const forecastHour = historyMode ? "0" : greatLakesControlValue("forecast");
  const depth = greatLakesShownDepth();
  const timeParams = greatLakesTimeParams();
  const dataStatus = historyMode ? null : await greatLakesDataStatus(modelsKey);
  if (loadRevision !== greatLakesLoadRevision) return;
  const dataVersion = historyMode ? `history:${timeParams.time}` : greatLakesDataVersion(dataStatus, layer);
  if (!historyMode && greatLakesAnimationActive()) {
    greatLakesConditionsRequest = new AbortController();
    setGreatLakesMapLoading(true);
    const isCurrent = () => loadRevision === greatLakesLoadRevision;
    const loaded = await loadGreatLakesAnimation(map, {
      layer,
      depth,
      signal: greatLakesConditionsRequest.signal,
      isCurrent,
      reload: () => loadGreatLakesConditions(map)
    });
    if (!isCurrent()) return;
    setGreatLakesMapLoading(false);
    if (loaded) {
      greatLakesActiveLayer = layer;
      greatLakesLoadedModelsKey = modelsKey;
      greatLakesLoadedDataVersion = dataVersion;
    }
    return;
  }
  const cacheKey = JSON.stringify({ layer, forecastHour, depth, resolution, models: modelsKey, dataVersion });
  const temperatureBackground = layer === "currents" && greatLakesControlValue("current-background") === "temperature";
  const temperatureCacheKey = JSON.stringify({ layer: "temperature", forecastHour, depth, resolution: CURRENT_BACKGROUND_RESOLUTION, models: modelsKey, dataVersion });
  const freshCache = cachedGreatLakesPayload;
  const cacheHit = freshCache(cacheKey) && (!temperatureBackground || freshCache(temperatureCacheKey));
  greatLakesConditionsRequest = cacheHit ? null : new AbortController();
  setGreatLakesStatus(historyMode ? "Loading saved conditions…" : "Loading NOAA forecast…");
  setGreatLakesMapLoading(true, historyMode ? "Loading saved conditions…" : undefined);
  // Keep the current drawing during a refresh, including a failed request.
  const retainingPrevious = greatLakesActiveLayer === layer && greatLakesLoadedModelsKey === modelsKey;
  if (!retainingPrevious) clearGreatLakesVisuals();
  const fetchLayer = async (key, request) => {
    const cachedPayload = freshCache(key);
    if (cachedPayload) return cachedPayload;
    const fetched = await window.noaaGreatLakesApi.conditions({ ...request, forecastHour, depth, models: modelsKey, dataVersion, ...timeParams, signal: greatLakesConditionsRequest.signal });
    cacheGreatLakesPayload(key, fetched);
    return fetched;
  };
  try {
    const [payload, temperaturePayload] = await Promise.all([
      fetchLayer(cacheKey, { layer, resolution }),
      temperatureBackground ? fetchLayer(temperatureCacheKey, { layer: "temperature", resolution: CURRENT_BACKGROUND_RESOLUTION }) : null
    ]);
    if (loadRevision !== greatLakesLoadRevision) return;
    if (layer === "air-temperature") {
      setAirTemperatureForecastHorizon(payload.metadata);
      if (Number(forecastHour) > greatLakesMaxForecastHour(layer)) {
        await loadGreatLakesConditions(map);
        return;
      }
    }
    clearGreatLakesVisuals();
    greatLakesActiveLayer = layer;
    greatLakesLoadedModelsKey = modelsKey;
    greatLakesLayerAvailability = historyMode ? null : payload.metadata.availability?.state
      || (payload.metadata.models?.some((model) => !model.available) ? "error" : null);
    if (["temperature", "bottom-temperature", "air-temperature"].includes(layer)) {
      setTemperatureLegendRange(payload.metadata, layer === "air-temperature" ? "airTemperature" : "waterTemperature");
      renderGreatLakesTemperatureRasters(layer === "air-temperature" ? greatLakesAirTemperatureRasters(payload) : payload.rasters || [], loadRevision, layer);
    }
    else if (layer === "thermocline") {
      setThermoclineLegendRange(payload.metadata);
      renderGreatLakesThermoclineRasters(payload.rasters || [], loadRevision);
    }
    else if (layer === "upwelling") {
      renderGreatLakesUpwellingRasters(payload.rasters || [], loadRevision);
    }
    else if (layer === "waves") {
      setWaveLegendRange(payload.metadata);
      renderGreatLakesWaveRasters(payload.rasters || [], loadRevision);
      const display = greatLakesControlValue("wave-display");
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (display === "flow" && !reduced && (payload.arrows || []).length) greatLakesParticleLayer = createParticleLayer(map, waveParticleFields(payload.arrows), {
        clipToGreatLakesWater: true,
        particleColor: waveParticleColorParts(savedWaveParticleColor())
      }).addTo(map);
      else if (display === "arrows" && (payload.arrows || []).length) greatLakesWaveArrowLayer = createWaveArrowLayer(map, payload.arrows).addTo(map);
    }
    else if (layer === "wind") {
      setWindLegendRange(payload.metadata);
      drawPaletteRasters(payload.rasters || [], "wind", { opacity: 0.8, className: "great-lakes-wind-raster" });
      showWindPayload(payload, { historical: historyMode });
    }
    else {
      setCurrentLegendRange(payload.metadata);
      setCurrentSpeedMax(payload.metadata);
      if (temperaturePayload) {
        setTemperatureLegendRange(temperaturePayload.metadata);
        renderGreatLakesTemperatureRasters(temperaturePayload.rasters || [], loadRevision, "currents");
      } else if (greatLakesControlValue("current-background") !== "none") {
        renderGreatLakesCurrentRasters(payload.rasters || [], loadRevision);
      }
      const display = greatLakesControlValue("current-display");
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (display === "flow" && !reduced && (payload.fields || []).length) greatLakesParticleLayer = createParticleLayer(map, payload.fields).addTo(map);
      else if (display !== "off") renderGreatLakesCurrents(payload.data || [], map.getZoom());
    }
    const { message, error } = noaaDataStatus([payload.metadata, temperaturePayload?.metadata],
      historyMode ? [] : statusRunTimes(dataStatus, layer), { historical: historyMode });
    setGreatLakesStatus(historyMode ? `${message} Past samples show the surface.`.trim() : message, error);
    greatLakesLoadedDataVersion = dataVersion;
    announceGreatLakesLayer(layer, payload.metadata || {}, temperaturePayload?.metadata || (layer === "temperature" ? payload.metadata : null));
  } catch (error) {
    if (loadRevision !== greatLakesLoadRevision || error.name === "AbortError") return;
    greatLakesLayerAvailability = "error";
    if (historyMode && layer === "wind") showWindPayload(null, { historical: true });
    if (layer === "air-temperature" && error.message.startsWith("Lakes-only air temperature")) setGreatLakesStatus(error.message, true);
    else if (historyMode) setGreatLakesStatus(/\(404\)/.test(error.message) ? "This layer was not saved at that time. Pick another sample." : "Saved conditions are unavailable right now.", true);
    else setGreatLakesStatus(retainingPrevious
      ? "NOAA data is unavailable right now; showing the most recent available."
      : "NOAA data is unavailable right now.", true);
  } finally {
    if (loadRevision === greatLakesLoadRevision) setGreatLakesMapLoading(false);
  }
}

export function setup() {
  renderGreatLakesConditionsHost();

  document.addEventListener("change", (event) => {
    const control = event.target.closest("[data-gl-layer], [data-gl-depth]");
    if (!control) return;
    const host = renderGreatLakesConditionsHost();
    if (!host) return;
    syncGreatLakesLayerControls(host);
    if (!ui.map) return;
    // Once the map control is attached its own listeners load the layer; a
    // second load here made the server build every change twice.
    if (!greatLakesConditionsControl) loadGreatLakesConditions(ui.map);
    if (greatLakesControlValue("layer") === "currents") {
      greatLakesActiveLayer = "currents";
      greatLakesLoadedModelsKey = GREAT_LAKES_ALL_MODELS.join(",");
    }
  });

  document.addEventListener("input", (event) => {
    const depthSlider = event.target.closest("[data-gl-depth]");
    if (!depthSlider) return;
    const label = greatLakesDepthSliderLabel(depthSlider.value);
    document.querySelector("[data-gl-depth-label]").textContent = label;
    depthSlider.setAttribute("aria-valuetext", label);
  });

  document.addEventListener("click", async (event) => {
    const button = event.target.closest("[data-gl-point-forecast-layer]");
    if (!button) return;
    const layer = button.dataset.glPointForecastLayer;
    if (layer !== "wind" && layer !== "waves") return;
    await showGreatLakesPointForecast({
      layer,
      latitude: Number(button.dataset.glPointForecastLat),
      longitude: Number(button.dataset.glPointForecastLon)
    });
  });

  document.addEventListener("click", async (event) => {
    const button = event.target.closest("[data-gl-profile-lat]");
    if (!button) return;
    // The card's button keeps its icon; only its text changes while loading.
    const label = button.querySelector("span") || button;
    const original = label.textContent;
    button.disabled = true;
    label.textContent = "Loading…";
    try {
      const profile = await window.noaaGreatLakesApi.profile({ forecastHour: greatLakesForecastHour(), latitude: button.dataset.glProfileLat, longitude: button.dataset.glProfileLon, models: greatLakesLoadedModelsKey, ...greatLakesTimeParams() });
      if (!profile.available) {
        showWaterColumnUnavailableDialog();
        label.textContent = original;
        return;
      }
      showWaterColumnDialog(profile);
      label.textContent = original;
    } catch {
      showWaterColumnUnavailableDialog();
      label.textContent = original;
    }
    finally { button.disabled = false; }
  });

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-gl-current-profile-lat]");
    if (!button) return;
    showGreatLakesCurrentProfile({
      latitude: Number(button.dataset.glCurrentProfileLat),
      longitude: Number(button.dataset.glCurrentProfileLon)
    });
  });

  window.ensureGreatLakesConditions = ensureGreatLakesConditions;

  let attempts = 0;
  const attachWhenMapReady = setInterval(() => {
    attempts += 1;
    if (ui.map) {
      ensureGreatLakesConditions(ui.map);
      clearInterval(attachWhenMapReady);
    } else if (attempts > 600) {
      clearInterval(attachWhenMapReady);
    }
  }, 50);

  window.getGreatLakesMapInspection = greatLakesMapInspection;
}
